/* ==========================================================================
   Milk & Honey — site scripts

   1.  Plugins & helpers
   1a. Smooth scroll (Lenis) + scroll lock / scrollTo helpers
   2.  Header scroll state
   3.  Burger menu
   4.  Modals
   5.  Custom cursor
   6.  Logos marquee
   7.  Scroll text reveal
   8.  Artists (hover-driven)
   9.  Team: hover zoom + caption, click opens contact modal
   10. Milk & Honey accordion slider
   11. Infinite cards grid
   12. Layered image slider
   13. Cascading slider
   14. Number odometer
   15. ScrollTrigger refresh-order fix
   16. Hits grid row counts
   17. Form floating labels
   18. Contact form: validation & submit
   19. FAQ accordion
   20. Football clients: hover + person details modal
   21. Ventures text: show more / show less
   22. Music intro: photo parallax
   23. Section nav: sidebar progress, menu highlight & anchors
   24. Legal pages: scroll line reveal
   25. Universal track slider
   26. Show more: collection reveal
   27. Stat items: scroll fade-in
   28. Footer cities: scroll to the offices accordion
   29. Header reveal
   30. Card reveal: collection cards rise and fade in, cascading
   31. Hits: vinyl slides out on hover
   32. Line drawing: [data-draw-svg] inline svgs drawn as one line on scroll
   33. Team order editor: drag members, save the order to the CMS
   ========================================================================== */

/* --------------------------------------------------------------------------
   1. Plugins & helpers
   -------------------------------------------------------------------------- */

// Guarded registration: a missing plugin should disable its feature,
// not crash the whole file with a ReferenceError
if (window.gsap && window.Observer) gsap.registerPlugin(Observer)
if (window.gsap && window.ScrollTrigger) gsap.registerPlugin(ScrollTrigger)
if (window.gsap && window.CustomEase) {
  gsap.registerPlugin(CustomEase)
  CustomEase.create('osmo', 'M0,0 C0.625,0.05 0,1 1,1')
}

function onReady(fn) {
  document.addEventListener('DOMContentLoaded', fn)
}

function toArray(selector, root) {
  return Array.prototype.slice.call(
    (root || document).querySelectorAll(selector)
  )
}

function debounce(fn, delay) {
  var timer
  return function () {
    clearTimeout(timer)
    timer = setTimeout(fn, delay)
  }
}

// Shrinks the element's font until its content fits the box (a long name
// overflowing its modal column). Resets first, so a short text after a
// long one returns to the design size; bails out when the element is not
// measurable (display: none somewhere above)
function fitText(el, minPx) {
  el.style.fontSize = ''
  if (!el.clientWidth) return

  var size = parseFloat(getComputedStyle(el).fontSize)
  var floor = Math.max(minPx || 0, size * 0.5)

  while (size > floor && el.scrollWidth > el.clientWidth + 1) {
    size -= 1
    el.style.fontSize = size + 'px'
  }
}

/* --------------------------------------------------------------------------
   1a. Smooth scroll: Lenis lerps the window scroll (lerp 0.08, wheel ×0.8 —
   the hishaam.webflow.io feel) and is ticked by the GSAP ticker, so the
   scroll and ScrollTrigger share one clock. Native scroll stays the source
   of truth: pageYOffset, ScrollTrigger, fixed and sticky elements all work
   as before, and touch is left native (Lenis' default). Two helpers wrap
   the differences for the rest of the file — scrollLock() for the burger
   and modals (counted, so overlapping locks can't unlock early) and
   smoothScrollTo() for anchors. Both fall back to native scrolling when
   Lenis is off: missing, reduced motion, or a phone-width viewport — the
   phone keeps its native scroll entirely (Lenis is created/destroyed as
   the viewport crosses Webflow's 767px breakpoint).
   -------------------------------------------------------------------------- */

var lenis = null
var scrollLocks = 0 // open overlays (burger, modals); >0 = page scroll locked

;(function () {
  if (!window.Lenis || !window.gsap || !window.matchMedia) return
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return

  var phone = window.matchMedia('(max-width: 767px)')

  function create() {
    if (lenis) return
    lenis = new Lenis({ lerp: 0.08, wheelMultiplier: 0.8 })
    if (window.ScrollTrigger) lenis.on('scroll', ScrollTrigger.update)
    if (scrollLocks > 0) lenis.stop() // created while an overlay is open
  }

  function destroy() {
    if (!lenis) return
    lenis.destroy() // removes its listeners and the html.lenis-* classes
    lenis = null
  }

  gsap.ticker.add(function (time) {
    if (lenis) lenis.raf(time * 1000)
  })
  gsap.ticker.lagSmoothing(0)

  if (!phone.matches) create()
  var onChange = function (e) {
    if (e.matches) destroy()
    else create()
  }
  if (phone.addEventListener) phone.addEventListener('change', onChange)
  else phone.addListener(onChange)

  // Overlays scroll on their own: a wheel inside them stays native instead
  // of being swallowed by the (stopped) page scroll
  onReady(function () {
    toArray('.modal__in, .menu').forEach(function (el) {
      el.setAttribute('data-lenis-prevent', '')
    })
  })
})()

function scrollLock(lock) {
  scrollLocks = Math.max(0, scrollLocks + (lock ? 1 : -1))
  var locked = scrollLocks > 0
  document.documentElement.classList.toggle('is-ovh', locked)
  document.body.classList.toggle('is-ovh', locked)
  if (lenis) {
    if (locked) lenis.stop()
    else lenis.start()
  }
}

// Lifts the CSS reveal-FOUC guard (app.sass): the element is now owned by
// a JS animation, which controls its visibility from here on
function revealReady(el) {
  if (el && el.classList) el.classList.add('is-reveal-ready')
}

function smoothScrollTo(top) {
  if (lenis) {
    lenis.scrollTo(top)
  } else if ('scrollBehavior' in document.documentElement.style) {
    window.scrollTo({ top: top, behavior: 'smooth' })
  } else {
    window.scrollTo(0, top)
  }
}

/* --------------------------------------------------------------------------
   2. Header: .is-active after scrolling past the threshold; .is-scrolled
   the moment scrolling starts at all — the logo text folds away on it,
   leaving the icon centered (pure CSS, see app.sass)
   -------------------------------------------------------------------------- */

;(function () {
  var THRESHOLD = 100
  var SCROLLED = 8 // anything past a hair counts as "started scrolling"

  onReady(function () {
    var header = document.querySelector('.header')
    if (!header) return

    function onScroll() {
      header.classList.toggle('is-active', window.pageYOffset > THRESHOLD)
      header.classList.toggle('is-scrolled', window.pageYOffset > SCROLLED)
    }

    onScroll() // handle a page opened mid-scroll
    window.addEventListener('scroll', onScroll, { passive: true })
  })
})()

/* --------------------------------------------------------------------------
   3. Burger menu: toggles .is-active on button/menu, scrollLock() (1a)
   locks page scroll.
   Menu items animate in with GSAP; closing resets instantly (no reverse).
   -------------------------------------------------------------------------- */

;(function () {
  onReady(function () {
    var burger = document.querySelector('.btn-burger')
    if (!burger) return

    var menu = document.querySelector('.menu')
    var items = menu ? menu.querySelectorAll('.menu__item') : []
    var numbers = menu ? menu.querySelectorAll('.menu__number') : []

    // Explicit initial state + .to() tweens: reliable replay on fast toggling.
    // Numbers are NOT part of the reveal: only the active section's numeral
    // is ever visible, and section 23 owns showing/hiding it — its inline
    // state survives this timeline, so the numeral rides in with its item
    var menuTl = null
    if (window.gsap && items.length) {
      gsap.set(items, { yPercent: 110, autoAlpha: 0 })
      gsap.set(numbers, { xPercent: -60, autoAlpha: 0 })

      menuTl = gsap.timeline({ paused: true })
      menuTl.to(items, {
        yPercent: 0,
        autoAlpha: 1,
        duration: 0.7,
        stagger: 0.09,
        ease: 'power4.out',
      })
    }

    burger.addEventListener('click', function (e) {
      e.preventDefault()
      var opened = burger.classList.toggle('is-active')
      scrollLock(opened)

      if (opened) {
        if (menu) menu.classList.add('is-active')
        if (menuTl) menuTl.play()
      } else {
        if (menu) menu.classList.remove('is-active')
        if (menuTl) menuTl.pause(0) // reset for the next opening
      }
    })
  })
})()

/* --------------------------------------------------------------------------
   4. Modals: [data-modal-show="#id"] opens, [data-modal-close] / backdrop /
   Escape closes. Open state is a CSS class (.is-active) — all easing lives
   in CSS, so no inline styles can get stuck. Optional per-modal line reveal
   via [data-modal-line-animation-selectors] (SplitText). Opener clicks are
   delegated, so a data-modal-show added at runtime (football clients get
   theirs from section 20) works the same as one from the markup.
   -------------------------------------------------------------------------- */

;(function () {
  onReady(function () {
    var current = null

    // One lock per open session: switching modals keeps the same lock
    function lockScroll(lock) {
      scrollLock(lock)
    }

    // Split listed selectors into masked lines once, then replay on each open
    function animateLines(modal) {
      if (!window.gsap || !window.SplitText) return
      gsap.registerPlugin(SplitText) // mask: 'lines' needs registration

      var attr = modal.getAttribute('data-modal-line-animation-selectors')
      if (!attr) return

      if (!modal._lineTl) {
        modal._splits = []
        var lines = []
        attr.split(',').forEach(function (sel) {
          sel = sel.trim()
          if (!sel) return
          toArray(sel, modal).forEach(function (el) {
            var split = new SplitText(el, {
              type: 'lines',
              mask: 'lines',
              linesClass: 'line',
            })
            modal._splits.push(split)
            lines = lines.concat(split.lines)
          })
        })
        if (!lines.length) return

        modal._lineTl = gsap.timeline({ paused: true })
        modal._lineTl.from(
          lines,
          {
            yPercent: 110,
            duration: 0.8,
            stagger: 0.06,
            ease: 'expo.out',
          },
          0.25 // small lead while the panel slides in
        )
      }

      modal._lineTl.restart()
    }

    function resetLines(modal) {
      if (modal && modal._lineTl) modal._lineTl.pause(0)
    }

    // Re-split on resize: panel width changes, so lines must be recalculated
    window.addEventListener(
      'resize',
      debounce(function () {
        toArray('.modal').forEach(function (m) {
          if (m._splits) {
            m._splits.forEach(function (s) {
              s.revert()
            })
            m._splits = null
          }
          if (m._lineTl) {
            m._lineTl.kill()
            m._lineTl = null
          }
        })
      }, 200)
    )

    function open(modal) {
      if (current === modal) return
      if (current) {
        current.classList.remove('is-active')
        resetLines(current)
      } else {
        lockScroll(true)
      }
      current = modal
      modal.classList.add('is-active')
      animateLines(modal)
    }

    function close() {
      if (!current) return
      current.classList.remove('is-active')
      resetLines(current)
      current = null
      lockScroll(false)
    }

    // Delegated and read per click: openers may receive data-modal-show at
    // runtime, and this document-level handler runs after any handlers on
    // the opener itself — content fills (section 20) land before the open
    document.addEventListener('click', function (e) {
      var opener = e.target.closest('[data-modal-show]')
      if (!opener) return
      // [data-modal-open-ignore] inside an opener is an opt-out zone: a
      // click there (a real link within a modal-opening section) keeps its
      // own default action and the modal stays closed
      var ignored = e.target.closest('[data-modal-open-ignore]')
      if (ignored && opener.contains(ignored)) return
      e.preventDefault()
      var sel = opener.getAttribute('data-modal-show')
      var modal = sel && document.querySelector(sel)
      if (modal) open(modal)
    })

    document.addEventListener('click', function (e) {
      if (
        e.target.closest('[data-modal-close]') ||
        e.target.closest('.modal__bg')
      ) {
        close()
      }
    })

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && current) close()
    })
  })
})()

/* --------------------------------------------------------------------------
   5. Custom cursor: [data-cursor-variant] blocks already exist in the DOM.
   They are fixed, follow the mouse, and the one matching the hovered
   [data-custom-cursor] value is shown. The value is live: it is re-read on
   every enter and watched for changes, so a block may swap its cursor on
   the fly by rewriting the attribute at runtime.
   -------------------------------------------------------------------------- */

;(function () {
  onReady(function () {
    if (!window.gsap) return

    var activators = document.querySelectorAll('[data-custom-cursor]')
    if (!activators.length) return

    // Fine pointers only, no touch screens
    if (window.matchMedia && !window.matchMedia('(pointer: fine)').matches)
      return

    var cursors = {}
    toArray('[data-cursor-variant]').forEach(function (el) {
      cursors[el.getAttribute('data-cursor-variant')] = el
    })

    var followers = []
    Object.keys(cursors).forEach(function (key) {
      var el = cursors[key]

      gsap.set(el, {
        position: 'fixed',
        top: 0,
        left: 0,
        zIndex: 9999,
        xPercent: -50,
        yPercent: -50,
        pointerEvents: 'none',
        autoAlpha: 0,
      })

      followers.push({
        key: key,
        el: el,
        xTo: gsap.quickTo(el, 'x', { duration: 0.4, ease: 'power3' }),
        yTo: gsap.quickTo(el, 'y', { duration: 0.4, ease: 'power3' }),
      })
    })
    if (!followers.length) return

    window.addEventListener('mousemove', function (e) {
      followers.forEach(function (f) {
        f.xTo(e.clientX)
        f.yTo(e.clientY)
      })
    })

    function show(key) {
      followers.forEach(function (f) {
        gsap.to(f.el, {
          autoAlpha: f.key === key ? 1 : 0,
          duration: 0.3,
          ease: 'power3',
        })
      })
    }

    function hideAll() {
      followers.forEach(function (f) {
        gsap.to(f.el, { autoAlpha: 0, duration: 0.3, ease: 'power3' })
      })
    }

    var hovered = null

    activators.forEach(function (el) {
      el.addEventListener('mouseenter', function () {
        hovered = el
        show(el.getAttribute('data-custom-cursor'))
      })
      el.addEventListener('mouseleave', function () {
        if (hovered === el) hovered = null
        hideAll()
      })
    })

    // Attribute changed mid-hover (no enter/leave will fire) — re-sync the
    // visible variant immediately
    var observer = new MutationObserver(function (mutations) {
      mutations.forEach(function (m) {
        if (m.target === hovered)
          show(hovered.getAttribute('data-custom-cursor'))
      })
    })
    activators.forEach(function (el) {
      observer.observe(el, { attributeFilter: ['data-custom-cursor'] })
    })
  })
})()

/* --------------------------------------------------------------------------
   6. Logos marquee: clone logos until the strip covers the container, then
   duplicate the whole strip and loop it with xPercent: -50 — the second half
   is an exact copy, so the restart is seamless.
   -------------------------------------------------------------------------- */

;(function () {
  var SPEED = 80 // px/s, constant regardless of logo count

  function build(track) {
    var wrap = track.parentNode
    if (!wrap) return

    wrap.style.overflow = 'hidden'

    // Restore the original markup before each rebuild
    if (track._marqueeHTML == null) track._marqueeHTML = track.innerHTML
    else track.innerHTML = track._marqueeHTML

    if (track._tween) track._tween.kill()
    gsap.set(track, { xPercent: 0 })

    // Inline layout wins over Webflow styles
    track.style.display = 'flex'
    track.style.flexWrap = 'nowrap'
    track.style.width = 'max-content'
    Array.prototype.forEach.call(track.children, function (node) {
      node.style.flexShrink = '0'
    })

    var base = Array.prototype.slice.call(track.children)
    if (!base.length) return
    var guard = 0
    while (track.scrollWidth < wrap.offsetWidth && guard < 60) {
      base.forEach(function (node) {
        track.appendChild(node.cloneNode(true))
      })
      guard++
    }

    Array.prototype.slice.call(track.children).forEach(function (node) {
      track.appendChild(node.cloneNode(true))
    })

    var distance = track.scrollWidth / 2
    track._tween = gsap.to(track, {
      xPercent: -50,
      duration: distance / SPEED,
      ease: 'none',
      repeat: -1,
    })
  }

  function initLogosMarquee(track) {
    if (!window.gsap) return
    build(track)

    window.addEventListener(
      'resize',
      debounce(function () {
        build(track)
      }, 200)
    )
  }

  onReady(function () {
    toArray('.music-intro-main__logos').forEach(initLogosMarquee)
  })
})()

/* --------------------------------------------------------------------------
   6b. Heading marquee: a section heading too wide for its box (FOOTBALL on
   a phone) loops smoothly instead of clipping — the section-6 pattern:
   copies until the strip covers the box, the whole strip doubled, and a
   seamless xPercent: -50 tween. A heading that fits stays a plain static
   heading; every resize re-decides from the original markup.
   -------------------------------------------------------------------------- */

;(function () {
  var SPEED = 60 // px/s
  var TARGETS = '.stat-intro__head .h1-italic, .ventures__head .h1-italic'
  var MOBILE = '(max-width: 479px)' // the loop is a phone-only effect

  function build(title) {
    var wasMarquee = !!title._tween
    var mobile = window.matchMedia && matchMedia(MOBILE).matches
    if (title._marqueeHTML == null) title._marqueeHTML = title.innerHTML

    // Desktop with no marquee in place: leave the DOM alone. Restoring the
    // original markup here used to wipe section 29's SplitText lines on
    // every font-load and resize, killing the header reveal on exactly
    // these headings until the next resize happened to re-split them
    if (!mobile && !wasMarquee) return

    // Measure from the original state — a marquee build or section 29's
    // line masks must not count as overflow
    title.innerHTML = title._marqueeHTML
    if (title._tween) {
      title._tween.kill()
      title._tween = null
    }
    title.style.overflow = ''

    if (!mobile || title.scrollWidth <= title.clientWidth + 1) {
      // Plain heading again — hand it back to the header reveal (29)
      if (title._revealRebuild) title._revealRebuild()
      return
    }

    title.style.overflow = 'hidden'

    var track = document.createElement('div')
    track.style.display = 'flex'
    track.style.flexWrap = 'nowrap'
    track.style.width = 'max-content'

    function copy() {
      var piece = document.createElement('div')
      piece.style.flexShrink = '0'
      piece.style.paddingRight = '0.5em'
      piece.style.whiteSpace = 'nowrap'
      piece.innerHTML = title._marqueeHTML
      return piece
    }

    track.appendChild(copy())
    title.innerHTML = ''
    title.appendChild(track)

    var guard = 0
    while (track.scrollWidth < title.clientWidth && guard < 30) {
      track.appendChild(copy())
      guard++
    }
    // The second half is an exact copy, so the restart is invisible
    Array.prototype.slice.call(track.children).forEach(function (node) {
      track.appendChild(node.cloneNode(true))
    })

    var distance = track.scrollWidth / 2
    title._tween = gsap.to(track, {
      xPercent: -50,
      duration: distance / SPEED,
      ease: 'none',
      repeat: -1,
    })
  }

  onReady(function () {
    if (!window.gsap) return
    if (
      window.matchMedia &&
      matchMedia('(prefers-reduced-motion: reduce)').matches
    )
      return

    var titles = toArray(TARGETS)
    if (!titles.length) return

    function buildAll() {
      titles.forEach(build)
    }

    buildAll()
    // Width settles once webfonts land
    if (document.fonts) document.fonts.ready.then(buildAll)
    window.addEventListener('resize', debounce(buildAll, 200))
  })
})()

/* --------------------------------------------------------------------------
   7. Scroll text reveal: per-char color scrub from data-reveal-text-from to
   data-reveal-text-to. [data-reveal-text-accent] spans end on their own color.
   -------------------------------------------------------------------------- */

;(function () {
  onReady(function () {
    if (!window.gsap || !window.ScrollTrigger || !window.SplitText) return
    gsap.registerPlugin(ScrollTrigger, SplitText)

    var targets = document.querySelectorAll('[data-reveal-text-to]')
    targets.forEach(function (heading) {
      var fromColor =
        heading.getAttribute('data-reveal-text-from') || 'rgba(4,8,10,0.16)'
      var toColor =
        heading.getAttribute('data-reveal-text-to') || 'rgba(4,8,10,0.9)'
      var scrollStart =
        heading.getAttribute('data-reveal-scroll-start') || 'top 90%'
      var scrollEnd =
        heading.getAttribute('data-reveal-scroll-end') || 'center 40%'
      var staggerValue =
        parseFloat(heading.getAttribute('data-reveal-stagger')) || 0.1

      heading.style.color = toColor

      // Accent chars inherit their final color from the span, so the same
      // .from() tween lands on the accent color automatically
      toArray('[data-reveal-text-accent]', heading).forEach(function (accent) {
        accent.style.color = accent.getAttribute('data-reveal-text-accent')
      })

      new SplitText(heading, {
        type: 'words, chars',
        autoSplit: true,
        onSplit: function (self) {
          return gsap.context(function () {
            gsap
              .timeline({
                scrollTrigger: {
                  scrub: true,
                  trigger: heading,
                  start: scrollStart,
                  end: scrollEnd,
                },
              })
              .from(self.chars, {
                color: fromColor,
                stagger: staggerValue,
                ease: 'linear',
              })
          })
        },
      })

      // The chars now carry the faint from-state — the CSS guard's stand-in
      // (app.sass) is no longer needed
      revealReady(heading)
    })
  })
})()

/* --------------------------------------------------------------------------
   8. Artists: hover-driven. The old pinned, scroll-stepped version is
   gone — it made the page feel slow. Hovering a name lights it up, shows
   its counter and thumbnail and swaps the background photo; the last
   hovered artist simply stays active when the pointer leaves, so the
   section never goes empty. First artist is active on load.
   -------------------------------------------------------------------------- */

;(function () {
  var DIM = 0.3 // inactive names: white at 30%
  var SHIFT = '7rem' // active name shifts right to make room for the counter
  var SHIFT_MOBILE = '4.5rem' // the desktop shift overshoots a narrow screen

  onReady(function () {
    if (!window.gsap) return
    // Two .artists sections can coexist while the collection markup
    // replaces the manual one — each initializes on its own
    toArray('.artists').forEach(initSection)
  })

  function initSection(section) {
    var items = toArray('.artists-item', section)
    if (!items.length) return

    // Collection counters arrive unpadded ("1") — the design wants "01"
    items.forEach(function (item) {
      var counter = item.querySelector('.artists-item__counter')
      if (!counter) return
      var label = counter.querySelector('.caption-l') || counter
      var text = label.textContent.trim()
      if (/^\d$/.test(text)) label.textContent = '0' + text
    })

    // Collection markup ships the background photos as data attributes on
    // .artists-item__data instead of ready-made .artists__photos sets —
    // those sets (desktop + mobile) are rebuilt here in item order, so the
    // crossfade logic below works unchanged. Every item adds an img even
    // with no url, keeping photo indexes aligned with item indexes
    if (!section.querySelector('.artists__photos')) {
      // The photo sets are inset: 0 overlays of the whole section. They go
      // in as its FIRST children: every positioned block that follows in
      // the DOM (.artists__in, .electronic-music, decorations) then paints
      // above them, no matter how deep Webflow nests the content wrappers
      var insertAfter = null
      ;[
        { attr: 'data-bg-desktop', extra: 'mobile-hidden' },
        { attr: 'data-bg-mobile', extra: 'mobile-visible' },
      ].forEach(function (set) {
        var group = document.createElement('div')
        group.className = 'artists__photos ' + set.extra
        var filled = false
        items.forEach(function (item) {
          var data = item.querySelector('.artists-item__data')
          var url = data ? data.getAttribute(set.attr) : ''
          var img = document.createElement('img')
          img.className = 'artists__photo'
          img.alt = ''
          img.loading = 'lazy'
          if (url) {
            img.src = url
            filled = true
          }
          group.appendChild(img)
        })
        if (filled) {
          section.insertBefore(
            group,
            insertAfter ? insertAfter.nextSibling : section.firstChild
          )
          insertAfter = group
        }
      })
    }

    // Background photos come in per-device sets now (.artists__photos
    // .mobile-visible / .mobile-hidden) — the active index is applied in
    // EACH set, and CSS decides which container is on screen. A flat list
    // without containers keeps working as one group
    var photoGroups = toArray('.artists__photos', section).map(function (
      group
    ) {
      return toArray('.artists__photo', group)
    })
    if (!photoGroups.length)
      photoGroups = [toArray('.artists__photo', section)]

    // The name element has survived several Webflow reworks under different
    // heading classes (h2-italic, h1-italic…) — match the family, not one
    // exact name, or the hover shift silently dies with the next rename
    var NAME = '[class*="-italic"]'
    if (!items[0].querySelector(NAME) && window.console)
      console.warn(
        '[artists] no *-italic name element inside .artists-item — the hover shift has nothing to move',
        items[0]
      )

    var current = -1

    // Initial states, so every transition animates from a known point
    items.forEach(function (item) {
      var counter = item.querySelector('.artists-item__counter')
      var visual = item.querySelector('.artists-item__visual')
      gsap.set(item, { opacity: DIM })
      if (counter) gsap.set(counter, { opacity: 0, x: -20 })
      if (visual) gsap.set(visual, { autoAlpha: 0, scale: 1.06 })
    })
    photoGroups.forEach(function (group) {
      gsap.set(group, { autoAlpha: 0 })
    })

    // A lazy image inside display: none never starts loading — on mobile
    // the first tap would open an empty box and jump once the file lands.
    // Warmed up front instead
    items.forEach(function (item) {
      var photo = item.querySelector('.artists-item__photo')
      if (photo && photo.loading === 'lazy') photo.loading = 'eager'
    })

    function setActive(index) {
      if (index === current) return
      current = index

      var mobileView =
        window.matchMedia && matchMedia('(max-width: 479px)').matches
      var shift = mobileView ? SHIFT_MOBILE : SHIFT

      // overwrite: 'auto' everywhere — rapid hovering never stacks tweens
      items.forEach(function (item, i) {
        var active = i === index
        var name = item.querySelector(NAME)
        var counter = item.querySelector('.artists-item__counter')
        var visual = item.querySelector('.artists-item__visual')

        gsap.to(item, {
          opacity: active ? 1 : DIM,
          duration: 0.7,
          ease: 'expo.out',
          overwrite: 'auto',
        })
        if (name) {
          gsap.to(name, {
            x: active ? shift : 0,
            duration: 0.8,
            ease: 'expo.out',
            overwrite: 'auto',
          })
        }
        if (counter)
          gsap.to(counter, {
            opacity: active ? 1 : 0,
            x: active ? 0 : -20,
            duration: 0.6,
            ease: 'expo.out',
            overwrite: 'auto',
          })
        if (visual) {
          // Mobile keeps inactive photos out of the flow (display: none via
          // .is-active), so the box has to GROW and COLLAPSE smoothly —
          // flipping display alone made the whole list jump. Desktop keeps
          // the plain class toggle: the visual is absolute there
          if (mobileView) {
            if (active) {
              var reopening = item.classList.contains('is-active')
              item.classList.add('is-active')
              gsap.killTweensOf(visual)
              gsap.set(visual, { clearProps: 'height' })
              if (!reopening)
                gsap.from(visual, {
                  height: 0,
                  duration: 0.55,
                  ease: 'power3.inOut',
                  onComplete: function () {
                    gsap.set(visual, { clearProps: 'height' })
                  },
                })
            } else if (item.classList.contains('is-active')) {
              gsap.killTweensOf(visual)
              gsap.to(visual, {
                height: 0,
                duration: 0.45,
                ease: 'power2.inOut',
                onComplete: function () {
                  item.classList.remove('is-active')
                  gsap.set(visual, { clearProps: 'height' })
                },
              })
            }
          } else {
            item.classList.toggle('is-active', active)
          }

          gsap.to(visual, {
            autoAlpha: active ? 1 : 0,
            scale: active ? 1 : 1.06,
            duration: active ? 0.9 : 0.5,
            ease: active ? 'expo.out' : 'power2.out',
            overwrite: 'auto',
          })
        } else {
          item.classList.toggle('is-active', active)
        }
      })

      // Background crossfade with a settle-in scale, active photo on top
      photoGroups.forEach(function (group) {
        group.forEach(function (photo, i) {
          var active = i === index
          photo.classList.toggle('is-active', active)
          gsap.set(photo, { zIndex: active ? 2 : 1 })
          if (active) gsap.set(photo, { scale: 1.05 })
          gsap.to(photo, {
            autoAlpha: active ? 1 : 0,
            scale: active ? 1 : undefined,
            duration: 0.9,
            ease: 'power2.inOut',
            overwrite: 'auto',
          })
        })
      })
    }

    setActive(0)

    items.forEach(function (item, i) {
      // Hover drives desktop; on touch (hover: none) the tap alone decides —
      // the mouseenter a tap synthesizes is unreliable and sticky, so it is
      // ignored there and the click below does the job
      item.addEventListener('mouseenter', function () {
        if (window.matchMedia && matchMedia('(hover: none)').matches) return
        setActive(i)
      })
      item.addEventListener('click', function (e) {
        // The whole list sits inside a wrapper link (artists__wrapper) —
        // a click on an item means "expand this artist", not "leave for
        // the external site", so the link's default is cancelled here
        e.preventDefault()
        setActive(i)
      })
    })
  }
})()

/* --------------------------------------------------------------------------
   9. Team: hover dims all items and zooms the hovered one; the card's
   caption (.music-intro-list__item-content: name and position) grows and
   fades in together with the photo, so the pair reads as one card rising
   out of the cell. The caption is laid out to the ZOOMED photo box — the
   photo grows by SCALE around the item center, so the caption is widened
   to SCALE × item and pulled out by half the growth on the left and
   bottom, which is exactly where the visible photo ends up — and it is
   sized to the bottom HALF of that box (the dark fade area, app.sass) and
   scaled from 1/SCALE to 1 around the photo's center — which is exactly
   the middle of the caption's top edge — with the same duration and ease
   as the photo, so their edges coincide on every frame. The card's arrow
   icon (.music-intro-list-wrapper__icon) rides along the same way: it is
   laid out in the zoomed photo's top-right corner, ICON_INSET from both
   edges, and scaled around the very same pivot — the photo center, which
   for it lies down-left of its box, so its transform-origin is set in px
   per item (hence the re-place on resize). Leaving the card runs the same
   tweens backwards. The icon is inside the item, so a click on it is a
   click on the card and opens the contact modal.
   The photo has pointer-events: none (app.sass), so the zoomed overhang
   never intercepts the pointer: hover is hit-tested against the items'
   own unscaled boxes and a neighbour covered by the big photo still
   reacts the moment the pointer is over its cell. Leaving a cell dims it
   after a short grace delay so crossing the grid gap does not flicker;
   entering another cell dims the previous one at once.
   A click on a card (or on the founder photo) fills #modal-team-contact
   from the card's data-modal-contact-* attributes and opens it straight
   away: the items carry data-modal-show, so section 4's delegated opener
   does the opening, and since it runs after these direct listeners the
   content is always in place first. (The old intermediate popup step is
   gone; .music-intro-list-popup stays hidden by CSS.)
   On mobile photos stay opaque and unscaled and no caption is shown — the
   tap goes straight to the modal the same way.
   A list marked data-cms-order is the order editor (section 33): none of
   this applies there — no dimming, zoom, caption or modal.
   -------------------------------------------------------------------------- */

;(function () {
  var DIM = 0.4
  var SCALE = 2.55 // design: ~346×421 card over a ~136×166 cell
  var ICON_INSET_REM = 1.25 // arrow icon offset from the zoomed card's top/right (matches the Webflow style)
  var DURATION = 0.4 // same for opacity and scale so they finish together
  var LEAVE_DELAY = 120 // ms grace before a left cell dims (gap crossing)
  var CONTACT_MODAL = '#modal-team-contact'
  var MOBILE = '(max-width: 479px)' // below this: no dim/zoom/caption

  onReady(function () {
    if (!window.gsap) return

    var list = document.querySelector('.music-intro-list')
    if (!list || list.hasAttribute('data-cms-order')) return

    var items = toArray('.music-intro-list__item', list)
    if (!items.length) return

    // The founder card (.music-intro-main) joins the same flow: its photo is
    // the clickable visual, it is filled from the same data-modal-contact-*
    // attributes, but it never dims/zooms — it is not part of the grid
    var main = document.querySelector('.music-intro-main')
    var MAIN_PHOTO = '.music-intro-main__photo'

    function isMain(item) {
      return item === main
    }

    function photoOf(item) {
      return isMain(item)
        ? item.querySelector(MAIN_PHOTO) || item.querySelector(':scope > img')
        : item.querySelector('.music-intro-list__photo')
    }

    function captionOf(item) {
      return item.querySelector('.music-intro-list__item-content')
    }

    function iconOf(item) {
      return item.querySelector('.music-intro-list-wrapper__icon')
    }

    // Everything that grows with the photo, for one item
    function overlayOf(item) {
      return [captionOf(item), iconOf(item)].filter(Boolean)
    }

    var mobile = false

    function isMobile() {
      return window.matchMedia && matchMedia(MOBILE).matches
    }

    // Opacity is left to applyMode: it differs per mode and has to be
    // re-applied when the breakpoint is crossed
    gsap.set(items, { position: 'relative', zIndex: 1 })

    // z-index only ever increases: the shrinking item stays above dimmed
    // neighbours while the newly hovered one stacks above it — no visible
    // layer switching
    var zCounter = 1

    // Caption geometry = the zoomed photo box (see the section note)
    var GROW = ((SCALE - 1) / 2) * 100 // % of the item the photo grows per side
    // Collapsed state: the caption is the bottom half of the zoomed photo
    // box, scaled down to the cell and invisible — highlight() grows it with
    // the photo. Its top edge runs through the photo center, so that edge's
    // middle is the scale pivot
    function placeOverlay(item) {
      var caption = captionOf(item)
      var icon = iconOf(item)
      if (caption)
        gsap.set(caption, {
          width: SCALE * 100 + '%',
          height: SCALE * 50 + '%',
          left: -GROW + '%',
          right: 'auto',
          bottom: -GROW + '%',
          transformOrigin: '50% 0%',
          scale: 1 / SCALE,
          autoAlpha: 0,
        })
      // The icon sits in the zoomed photo's top-right corner. Its pivot is
      // the photo center, expressed in its own box: from the icon's
      // top-left, the center is half a zoomed photo minus the inset (and
      // the icon's own size, horizontally) away
      if (icon) {
        var inset =
          ICON_INSET_REM *
          parseFloat(getComputedStyle(document.documentElement).fontSize)
        var halfW = (item.offsetWidth * SCALE) / 2
        var halfH = (item.offsetHeight * SCALE) / 2
        var top = item.offsetHeight / 2 - halfH + inset
        var right = item.offsetWidth / 2 - halfW + inset
        gsap.set(icon, {
          top: top + 'px',
          right: right + 'px',
          left: 'auto',
          bottom: 'auto',
          transformOrigin:
            -(halfW - inset - icon.offsetWidth) + 'px ' + (halfH - inset) + 'px',
          scale: 1 / SCALE,
          autoAlpha: 0,
        })
      }
    }

    function highlight(item) {
      if (isMain(item)) return
      var photo = photoOf(item)
      var overlay = overlayOf(item)
      gsap.killTweensOf([item, photo].concat(overlay))
      gsap.to(item, { opacity: 1, duration: DURATION, ease: 'power2.out' })
      if (photo)
        gsap.to(photo, { scale: SCALE, duration: DURATION, ease: 'power2.out' })
      if (overlay.length)
        gsap.to(overlay, {
          scale: 1,
          autoAlpha: 1,
          duration: DURATION,
          ease: 'power2.out',
        })
    }

    function dim(item) {
      if (isMain(item)) return
      var photo = photoOf(item)
      var overlay = overlayOf(item)
      gsap.killTweensOf([item, photo].concat(overlay))
      gsap.to(item, { opacity: DIM, duration: DURATION, ease: 'power2.out' })
      if (photo)
        gsap.to(photo, { scale: 1, duration: DURATION, ease: 'power2.out' })
      if (overlay.length)
        gsap.to(overlay, {
          scale: 1 / SCALE,
          autoAlpha: 0,
          duration: DURATION,
          ease: 'power2.out',
        })
    }

    // The cell under the pointer; only one is ever zoomed
    var current = null
    var leaveTimer = null

    function enter(item) {
      clearTimeout(leaveTimer)
      if (current && current !== item) dim(current)
      current = item
      gsap.set(item, { zIndex: ++zCounter })
      highlight(item)
    }

    function leave(item) {
      clearTimeout(leaveTimer)
      leaveTimer = setTimeout(function () {
        if (current === item) {
          dim(item)
          current = null
        }
      }, LEAVE_DELAY)
    }

    items.forEach(function (item) {
      item.addEventListener('mouseenter', function () {
        if (mobile) return // no dim/zoom below the breakpoint
        enter(item)
      })

      item.addEventListener('mouseleave', function () {
        if (mobile) return
        leave(item)
      })
    })

    // Off the grid entirely: no grace, drop the zoom right away
    list.addEventListener('mouseleave', function () {
      if (mobile || !current) return
      clearTimeout(leaveTimer)
      dim(current)
      current = null
    })

    // The contact modal shows the clicked member: photo, name, position,
    // location and a hidden form field naming who is being contacted
    var contactModal = document.querySelector(CONTACT_MODAL)
    if (!contactModal) return

    var modalPhoto = contactModal.querySelector('.modal__photo')
    var modalName = contactModal.querySelector('.modal__subhead .h4-italic')
    var modalDetails = contactModal.querySelectorAll('.modal__details .body-l')
    var modalMember = contactModal.querySelector(
      'input[name="team-member-name"]'
    )

    // The member's e-mail travels in a hidden field the same way the name
    // does — section 18 reads it to route the SendGrid delivery. Created
    // here so the Webflow form needs no extra markup
    var modalMemberEmail = contactModal.querySelector(
      'input[name="team-member-email"]'
    )
    if (!modalMemberEmail) {
      var memberForm = contactModal.querySelector('form')
      if (memberForm) {
        modalMemberEmail = document.createElement('input')
        modalMemberEmail.type = 'hidden'
        modalMemberEmail.name = 'team-member-email'
        memberForm.appendChild(modalMemberEmail)
      }
    }

    // The modal copy is line-split by section 4 on first open and cached on
    // the element (_splits/_lineTl) — that split is reverted BEFORE swapping
    // the text, so the next open re-splits the fresh content. A stale
    // success screen (and the copy section 18 cached for it) is dropped the
    // same way: a new member always starts from the empty form
    function fillContactModal(item) {
      if (contactModal._splits) {
        contactModal._splits.forEach(function (s) {
          s.revert()
        })
        contactModal._splits = null
      }
      if (contactModal._lineTl) {
        contactModal._lineTl.kill()
        contactModal._lineTl = null
      }

      if (window.jQuery)
        window
          .jQuery(contactModal)
          .find('[data-form-success]')
          .removeData('formCopy')

      var form = contactModal.querySelector('.form')
      if (form) form.classList.remove('is-success-sent')
      var success = contactModal.querySelector('.form__success')
      if (success) success.classList.remove('is-active')

      // The success screen hides duplicate success texts inline (section
      // 18) — undo that so every detail line is visible for the new member
      toArray('[data-form-success]', contactModal).forEach(function (el) {
        el.style.display = ''
      })

      var photo = photoOf(item)
      var name = item.getAttribute('data-modal-contact-name') || ''

      if (modalPhoto && photo) modalPhoto.src = photo.currentSrc || photo.src
      if (modalName) {
        modalName.textContent = name
        fitText(modalName)
      }

      // Details: [0] position, [1] the "•" divider, [2] location — the
      // divider hides whenever either side comes up empty
      var position = item.getAttribute('data-modal-contact-position') || ''
      var location = item.getAttribute('data-modal-contact-location') || ''

      if (modalDetails[0]) {
        modalDetails[0].textContent = position
        modalDetails[0].style.display = position ? '' : 'none'
      }
      if (modalDetails[2]) {
        modalDetails[2].textContent = location
        modalDetails[2].style.display = location ? '' : 'none'
      }
      if (modalDetails[1])
        modalDetails[1].style.display = position && location ? '' : 'none'

      if (modalMember) modalMember.value = name
      if (modalMemberEmail)
        modalMemberEmail.value =
          item.getAttribute('data-modal-contact-email') || ''
    }

    // Click → fill, then section 4 opens the modal via data-modal-show
    items.forEach(function (item) {
      item.setAttribute('data-modal-show', CONTACT_MODAL)
      item.addEventListener('click', function () {
        fillContactModal(item)
      })
    })

    // Only the founder photo is the trigger — the logo marquee below it
    // belongs to the same block but must not open the modal
    var mainPhoto = main ? photoOf(main) : null
    if (mainPhoto) {
      mainPhoto.setAttribute('data-modal-show', CONTACT_MODAL)
      mainPhoto.addEventListener('click', function () {
        fillContactModal(main)
      })
    }

    // Entering a mode resets what the other one left behind: dimming,
    // photo zoom and the caption geometry belong to the pointer flow
    function applyMode() {
      mobile = isMobile()
      clearTimeout(leaveTimer)
      current = null

      gsap.killTweensOf(items)
      items.forEach(function (item) {
        var photo = photoOf(item)
        var overlay = overlayOf(item)
        if (photo) gsap.killTweensOf(photo)
        if (overlay.length) gsap.killTweensOf(overlay)

        if (mobile) {
          if (photo) gsap.set(photo, { scale: 1 })
          if (overlay.length)
            gsap.set(overlay, {
              clearProps:
                'width,height,left,right,top,bottom,transform,transformOrigin,opacity,visibility',
              // (icon keeps its Webflow size; only placement props were set)
            })
        } else {
          placeOverlay(item)
        }
      })
      gsap.set(items, { opacity: mobile ? 1 : DIM })
    }

    applyMode()

    // The icon's pivot is in px: re-place idle cards when the layout changes
    window.addEventListener(
      'resize',
      debounce(function () {
        if (isMobile() !== mobile) {
          applyMode()
          return
        }
        if (mobile) return
        items.forEach(function (item) {
          if (item !== current) placeOverlay(item)
        })
      }, 150)
    )
  })
})()

/* --------------------------------------------------------------------------
   10. Milk & Honey accordion slider. Smoothness relies on:
   - cards keep a fixed pixel width (= final active column), so their content
     never reflows during the animation — the column just reveals it
     through overflow: hidden;
   - column widths are interpolated per frame by GSAP;
   - one timeline, cascading-slider choreography: both cards stay fully
     painted while the columns move — the shrinking/expanding column IS
     the transition (a clip, not a crossfade). Only the text plays extra:
     the old card's text drops out at once, the new card's text rises in
     near the end of the travel; the old card dissolves only in the last
     stretch, when its column is almost down to the tab.
   On mobile (≤479px — where the Webflow grid stacks to one column) the
   accordion turns vertical: the active item reveals its content by height,
   collapsed items are just their title row.
   -------------------------------------------------------------------------- */

;(function () {
  var TAB_REM = 8.25 // inactive tab width (matches the Webflow grid)
  var DURATION = 0.65
  var EASE = 'power3.inOut'
  var MOBILE = '(max-width: 479px)' // the width where Webflow stacks the grid

  onReady(function () {
    var container = document.querySelector('.milk-honey-slider__items')
    if (!container) return
    var items = toArray('.milk-honey-slider-item', container)
    if (!items.length) return

    // Webflow regenerates each photo's `sizes` from whichever breakpoint
    // was last edited; a stray value (166px on one office) shrinks that
    // image's intrinsic width, and since __visual sizes itself from
    // content, the whole card collapses into a strip. Normalize them so
    // every photo reports the same geometry
    toArray('.milk-honey-slider-item__photo', container).forEach(function (
      photo
    ) {
      if (photo.getAttribute('sizes'))
        photo.setAttribute('sizes', '(max-width: 734px) 100vw, 734px')
    })

    var current = 0
    var tl = null
    var mobile = false

    function isMobile() {
      return window.matchMedia && matchMedia(MOBILE).matches
    }

    function remPx(rem) {
      return (
        rem * parseFloat(getComputedStyle(document.documentElement).fontSize)
      )
    }

    function innerOf(item) {
      return item.querySelector('.milk-honey-slider-item__in')
    }

    function contentOf(item) {
      return item.querySelectorAll(
        '.milk-honey-slider-item__text, .milk-honey-slider-item__visual'
      )
    }

    function activeColWidth() {
      return container.clientWidth - (items.length - 1) * remPx(TAB_REM)
    }

    function sizeInners() {
      var w = activeColWidth()
      items.forEach(function (item) {
        var inner = innerOf(item)
        if (!inner) return
        var cs = getComputedStyle(item)
        inner.style.width =
          w - parseFloat(cs.paddingLeft) - parseFloat(cs.paddingRight) + 'px'
      })
    }

    function widthsFor(index) {
      var tab = remPx(TAB_REM)
      var active = activeColWidth()
      return items.map(function (_, i) {
        return i === index ? active : tab
      })
    }

    function applyWidths(ws) {
      container.style.gridTemplateColumns = ws
        .map(function (w) {
          return w + 'px'
        })
        .join(' ')
    }

    // Lock the row height so switching never causes a vertical jump
    function lockHeight() {
      container.style.minHeight = ''
      container.style.minHeight = container.offsetHeight + 'px'
    }

    function setActiveDesktop(index, animate) {
      if (tl) tl.kill()

      var prev = current
      current = index

      items.forEach(function (item, i) {
        item.classList.toggle('is-active', i === index)
      })

      var oldInner = prev !== index ? innerOf(items[prev]) : null

      // Visibility is re-set for ALL cards on every call, so leftovers can
      // never accumulate even with rapid clicking
      items.forEach(function (item, i) {
        var inner = innerOf(item)
        if (!inner) return
        var visible = i === index || (animate && i === prev)
        inner.classList.toggle('is-active', visible)
        if (visible && window.gsap && inner !== oldInner)
          gsap.set(inner, { clearProps: 'opacity,visibility' })
      })

      var newText = items[index].querySelector('.milk-honey-slider-item__text')
      var newVisual = items[index].querySelector(
        '.milk-honey-slider-item__visual'
      )
      var oldText = oldInner
        ? items[prev].querySelector('.milk-honey-slider-item__text')
        : null
      var from = widthsFor(prev)
      var to = widthsFor(index)

      if (!animate || !window.gsap) {
        applyWidths(to)
        if (oldInner) oldInner.classList.remove('is-active')
        if (window.gsap)
          gsap.set(contentOf(items[index]), { autoAlpha: 1, y: 0 })
        return
      }

      applyWidths(from)

      var proxy = { p: 0 }
      tl = gsap.timeline({
        onComplete: function () {
          if (oldInner) {
            oldInner.classList.remove('is-active')
            gsap.set(oldInner, { clearProps: 'opacity,visibility' })
          }
        },
      })

      // The photo stays painted the whole way — the expanding column
      // uncovers it, the reference slider's clip reveal
      if (newVisual) gsap.set(newVisual, { autoAlpha: 1, y: 0 })

      // Old text drops out at once (the reference heading has no exit
      // delay); the card itself stays painted under the shrinking column…
      if (oldText)
        tl.to(oldText, { autoAlpha: 0, duration: 0.25, ease: 'power1.out' }, 0)

      // …and dissolves only in the last stretch, when its column is
      // nearly down to the tab, so the swap to the title never pops
      if (oldInner)
        tl.to(
          oldInner,
          { autoAlpha: 0, duration: 0.3, ease: 'power1.inOut' },
          DURATION * 0.5
        )

      // Column widths, interpolated per frame
      tl.to(
        proxy,
        {
          p: 1,
          duration: DURATION,
          ease: EASE,
          onUpdate: function () {
            applyWidths(
              from.map(function (w, i) {
                return w + (to[i] - w) * proxy.p
              })
            )
          },
        },
        0
      )

      // New text rises in near the end of the travel — the reference
      // heading's delayed entrance (400ms into a 650ms move)
      if (newText)
        tl.fromTo(
          newText,
          { autoAlpha: 0, y: 16 },
          { autoAlpha: 1, y: 0, duration: 0.35, ease: 'power2.out' },
          DURATION * 0.55
        )
    }

    // Mobile: the stacked accordion reveals the active content by HEIGHT —
    // no column math applies here, and applyMode() has already wiped the
    // desktop inline styles that would strangle the one-column layout
    function setActiveMobile(index, animate) {
      if (tl) tl.kill()

      var prev = current
      current = index

      items.forEach(function (item, i) {
        item.classList.toggle('is-active', i === index)
      })

      var oldInner = prev !== index ? innerOf(items[prev]) : null
      var newInner = innerOf(items[index])

      items.forEach(function (item, i) {
        var inner = innerOf(item)
        if (!inner) return
        var visible = i === index || (animate && i === prev)
        inner.classList.toggle('is-active', visible)
      })

      if (!animate || !window.gsap) {
        if (window.gsap)
          items.forEach(function (item) {
            var inner = innerOf(item)
            if (inner)
              gsap.set(inner, { clearProps: 'height,opacity,visibility' })
            gsap.set(contentOf(item), {
              clearProps: 'opacity,visibility,transform',
            })
          })
        return
      }

      var newContent = contentOf(items[index])

      tl = gsap.timeline({
        onComplete: function () {
          if (oldInner) {
            oldInner.classList.remove('is-active')
            gsap.set(oldInner, { clearProps: 'height,opacity,visibility' })
          }
          // Height back to auto, so later reflows (fonts, rotation) fit
          if (newInner) gsap.set(newInner, { clearProps: 'height' })
        },
      })

      if (oldInner)
        tl.to(
          oldInner,
          { height: 0, autoAlpha: 0, duration: 0.45, ease: 'power2.inOut' },
          0
        )

      // .from() reads the natural auto height and rises to it from zero;
      // the item's overflow: hidden turns that into a clean reveal
      if (newInner)
        tl.from(
          newInner,
          { height: 0, duration: 0.65, ease: 'power3.inOut' },
          oldInner ? 0.15 : 0
        )

      if (newContent.length)
        tl.fromTo(
          newContent,
          { autoAlpha: 0, y: 16 },
          { autoAlpha: 1, y: 0, duration: 0.5, ease: 'power3.out', stagger: 0.06 },
          0.35
        )
    }

    function setActive(index, animate) {
      if (mobile) setActiveMobile(index, animate)
      else setActiveDesktop(index, animate)
    }

    // Entering a mode wipes the other mode's inline styles: the desktop
    // math (px column widths, locked min-height) would strangle the
    // one-column mobile layout, and stale heights would corrupt desktop
    function applyMode() {
      mobile = isMobile()

      if (mobile) {
        container.style.gridTemplateColumns = ''
        container.style.minHeight = ''
        items.forEach(function (item) {
          var inner = innerOf(item)
          if (inner) {
            inner.style.width = ''
            inner.style.height = ''
          }
        })
        setActiveMobile(current, false)
      } else {
        items.forEach(function (item) {
          var inner = innerOf(item)
          if (inner) inner.style.height = ''
        })
        sizeInners()
        setActiveDesktop(current, false)
        lockHeight()
      }
    }

    // Delegated click works even when items are <a href="#">
    container.addEventListener('click', function (e) {
      var item = e.target.closest('.milk-honey-slider-item')
      if (!item || !container.contains(item)) return
      e.preventDefault()
      var index = items.indexOf(item)
      if (index !== -1 && index !== current) setActive(index, true)
    })

    // Deep link for other code (the footer city links, section 28): opens
    // the office whose data-office-target matches the given name
    container._openOffice = function (name) {
      var target = String(name).trim().toLowerCase()
      for (var i = 0; i < items.length; i++) {
        var label = items[i].getAttribute('data-office-target') || ''
        if (label.trim().toLowerCase() === target) {
          if (i !== current) setActive(i, true)
          return true
        }
      }
      return false
    }

    applyMode()

    // Height may settle once images finish loading; the lock is desktop-only
    window.addEventListener('load', function () {
      if (!mobile) lockHeight()
    })

    window.addEventListener(
      'resize',
      debounce(function () {
        if (isMobile() !== mobile) {
          applyMode()
          return
        }
        if (!mobile) {
          sizeInners()
          applyWidths(widthsFor(current))
          lockHeight()
        }
      }, 150)
    )
  })
})()

/* --------------------------------------------------------------------------
   11. Infinite cards grid: endless draggable masonry. Wheel is intentionally
   NOT captured so the page keeps scrolling — the grid is explored by drag.
   -------------------------------------------------------------------------- */

function initInfiniteCardsGrid() {
  const wrappers = document.querySelectorAll('[data-infinite-grid-init]')

  const wheelSpeed = 0.6
  const dragSpeed = 1.2
  const gridOverscan = 1 // extra rows/columns generated outside the viewport
  const startOffsetY = 0.33 // vertical offset between columns
  const positionLerp = 0.05
  const xToYInfluence = 0.2
  const columnSpeedPattern = [1, 1, 0.9] // column parallax speeds
  const minCardScale = 0.5
  const scaleLerp = 0.02

  // The grid drifts on its own everywhere: cards travel from the bottom-
  // right towards the top-left, the wrap makes it endless. Interaction is
  // untouched — desktop drag rides on top of the drift, while touch stays
  // drift-only: the drag Observer runs with preventDefault, which would
  // swallow the vertical gesture and trap the page scroll
  const driftX = -14 // px/s, negative = leftwards
  const driftY = -10 // px/s, negative = upwards
  const isTouch = () =>
    !(window.matchMedia && matchMedia('(pointer: fine)').matches)

  wrappers.forEach((wrapper) => {
    const collection = wrapper.querySelector('[data-infinite-grid-collection]')
    const sourceList = wrapper.querySelector('[data-infinite-grid-list]')
    const originalItems = Array.from(
      sourceList.querySelectorAll('[data-infinite-grid-item]')
    ).map((item) => item.cloneNode(true))

    if (!collection || !sourceList || !originalItems.length) return

    // Every photo is cloned several times over the grid. The source markup
    // ships the <img>s lazy, and a lazy clone only asks for its file once
    // it drifts near the viewport — each copy then fires its own request
    // (served from cache, but a separate request all the same) and a clone
    // wrapping into view can land before its file is ready and flash
    // blank. So the clones load eagerly: the browser folds all copies of
    // one URL into a single fetch, and the originals are warmed once here
    // so every rebuild after a resize reuses the decoded images
    const uniqueSources = new Set()
    originalItems.forEach((item) => {
      item.querySelectorAll('img').forEach((img) => {
        img.loading = 'eager'
        img.decoding = 'async'
        if (img.currentSrc || img.src) uniqueSources.add(img.currentSrc || img.src)
      })
    })
    uniqueSources.forEach((src) => {
      const img = new Image()
      img.src = src
    })

    let observer
    let touchMode = false
    let cards = []
    let cardElements = []
    const timers = {}
    const size = {}
    const pos = {}
    const scale = { current: 1, target: 1 }

    function setStatus(status) {
      wrapper.setAttribute('data-infinite-grid-status', status)
    }

    function wrapValue(value, size) {
      return ((value % size) + size) % size
    }

    function createColumnSpeeds(columns) {
      return Array.from(
        { length: columns },
        (_, i) => columnSpeedPattern[i % columnSpeedPattern.length]
      )
    }

    // The collection tiles the grid as a blockW×blockH block (3×3 for
    // nine photos), read top-left to bottom-right in list order. Every
    // photo appears exactly once per block, so two copies of one shot are
    // never closer than a full block apart — the widest spacing the
    // collection size allows. This also survives the column parallax:
    // duplicates only ever share columns a whole block apart, and those
    // move at the same speed (the speed pattern repeats every 3), so the
    // spacing never drifts shut
    function createItemIndexes(columns, rows) {
      const total = originalItems.length
      const blockW = Math.ceil(Math.sqrt(total))
      const blockH = Math.ceil(total / blockW)
      return Array.from({ length: rows }, (_, row) =>
        Array.from(
          { length: columns },
          (_, column) =>
            ((column % blockW) + blockW * (row % blockH)) % total
        )
      )
    }

    function buildGrid() {
      if (observer) {
        observer.kill()
        observer = null
      }
      clearTimeout(timers.resize)
      clearTimeout(timers.scroll)
      clearTimeout(timers.scale)
      gsap.ticker.remove(updateGrid)

      // Re-evaluated on every build, so rotating a tablet or plugging in a
      // mouse switches the mode
      touchMode = isTouch()

      setStatus('loading')
      sourceList.innerHTML = ''

      const measureItem = originalItems[0].cloneNode(true)
      measureItem.style.position = 'absolute'
      measureItem.style.visibility = 'hidden'
      measureItem.style.pointerEvents = 'none'
      wrapper.appendChild(measureItem)

      const rect = measureItem.getBoundingClientRect()
      size.itemW = rect.width
      size.itemH = rect.height
      measureItem.remove()

      if (!size.itemW || !size.itemH) return

      size.builtW = wrapper.clientWidth
      size.builtH = wrapper.clientHeight

      // Column/row counts round up to whole tiles (see createItemIndexes):
      // the endless wrap then continues the tiling seamlessly, keeping the
      // duplicate-spacing guarantee across the seams too
      const total = originalItems.length
      const blockW = Math.ceil(Math.sqrt(total))
      const blockH = Math.ceil(total / blockW)
      const columns =
        Math.ceil(
          Math.max(
            1,
            Math.ceil(wrapper.clientWidth / size.itemW) + gridOverscan * 2
          ) / blockW
        ) * blockW
      const rows =
        Math.ceil(
          Math.max(
            Math.ceil(wrapper.clientHeight / size.itemH) + gridOverscan * 2,
            Math.ceil(total / columns)
          ) / blockH
        ) * blockH
      const itemIndexes = createItemIndexes(columns, rows)
      const columnSpeeds = createColumnSpeeds(columns)
      const fragment = document.createDocumentFragment()
      const centerColumn = Math.floor(columns / 2)
      const centerRow = Math.floor(rows / 2)

      size.totalW = columns * size.itemW
      size.totalH = rows * size.itemH
      cards = []
      cardElements = []

      gsap.set(collection, { x: 0, y: 0, force3D: true })

      collection.style.width = `${size.totalW}px`
      collection.style.height = `${size.totalH}px`

      for (let row = 0; row < rows; row++) {
        for (let column = 0; column < columns; column++) {
          const item = originalItems[itemIndexes[row][column]].cloneNode(true)
          const card = item.querySelector('[data-infinite-grid-card]')

          cards.push({
            baseX: column * size.itemW,
            baseY: row * size.itemH,
            startY: (column - centerColumn) * size.itemH * startOffsetY,
            ySpeed: columnSpeeds[column],
            xSetter: gsap.quickSetter(item, 'x', 'px'),
            ySetter: gsap.quickSetter(item, 'y', 'px'),
          })

          if (card) cardElements.push(card)
          if (cards.length > originalItems.length)
            item.setAttribute('aria-hidden', 'true')

          fragment.appendChild(item)
        }
      }

      sourceList.appendChild(fragment)

      gsap.set(cardElements, { force3D: true })

      pos.startX =
        wrapper.clientWidth * 0.5 - centerColumn * size.itemW - size.itemW * 0.5
      pos.startY =
        wrapper.clientHeight * 0.5 - centerRow * size.itemH - size.itemH * 0.5
      pos.x = pos.startX
      pos.y = pos.startY
      pos.targetX = pos.x
      pos.targetY = pos.y
      scale.current = 1
      scale.target = 1

      updateGrid()
      gsap.ticker.add(updateGrid)

      requestAnimationFrame(() => {
        setStatus('idle')
      })

      // No drag on touch: the gesture is left to the page scroll
      if (touchMode) return

      observer = Observer.create({
        target: wrapper,
        type: 'touch,pointer', // no 'wheel': the page must keep scrolling
        preventDefault: true,
        dragMinimum: 3,
        onPress() {
          setStatus('dragging')
        },
        onRelease() {
          setStatus('idle')
        },
        onStop() {
          setStatus('idle')
        },
        onChange: handleMovement,
      })
    }

    function updateGrid(time, deltaTime) {
      // The drift never stops on its own — it only yields to the pointer
      // while a drag is in progress, so the hand stays in full control
      if (wrapper.getAttribute('data-infinite-grid-status') !== 'dragging') {
        // Frame time is capped: a backgrounded tab returns with a huge
        // delta, which would teleport the grid on the first frame back
        const seconds = Math.min(deltaTime || 16.7, 50) / 1000
        pos.targetX += driftX * seconds
        pos.targetY += driftY * seconds
      }

      pos.x += (pos.targetX - pos.x) * positionLerp
      pos.y += (pos.targetY - pos.y) * positionLerp
      scale.current += (scale.target - scale.current) * scaleLerp

      const offsetX = size.itemW * gridOverscan
      const offsetY = size.itemH * gridOverscan
      const scrollY = pos.y - pos.startY

      cards.forEach(({ baseX, baseY, startY, ySpeed, xSetter, ySetter }) => {
        xSetter(wrapValue(baseX + pos.x + offsetX, size.totalW) - offsetX)
        ySetter(
          wrapValue(
            baseY + pos.startY + startY + scrollY * ySpeed + offsetY,
            size.totalH
          ) - offsetY
        )
      })

      gsap.set(cardElements, { scale: scale.current })
    }

    function handleMovement(self) {
      const isWheel = self.event.type === 'wheel'
      const speed = isWheel ? wheelSpeed : dragSpeed
      const deltaX = gsap.utils.clamp(-80, 80, self.deltaX * speed)
      const deltaY = gsap.utils.clamp(-80, 80, self.deltaY * speed)
      const moveX = isWheel ? -deltaX : deltaX
      const moveY = isWheel ? -deltaY : deltaY
      const strength = gsap.utils.clamp(
        0,
        1,
        Math.max(Math.abs(deltaX), Math.abs(deltaY)) / 80
      )

      if (isWheel) {
        setStatus('scrolling')

        clearTimeout(timers.scroll)
        timers.scroll = setTimeout(() => {
          setStatus('idle')
        }, 200)
      }

      scale.target = gsap.utils.interpolate(1, minCardScale, strength)

      clearTimeout(timers.scale)
      timers.scale = setTimeout(() => {
        scale.target = 1
      }, 120)

      pos.targetX += moveX
      pos.targetY += moveY + moveX * xToYInfluence
    }

    function handleMouseLeave() {
      setStatus('idle')
      scale.target = 1

      if (observer) {
        observer.disable()
        observer.enable()
      }
    }

    window.addEventListener('resize', () => {
      clearTimeout(timers.resize)
      timers.resize = setTimeout(() => {
        // Mobile fires resize while the page scrolls — the URL bar folding
        // changes the window height. The wrapper is 100svh, so its own box
        // is untouched then; rebuilding would blink the grid and restart
        // the drift from zero. Only a real box change rebuilds
        if (
          wrapper.clientWidth === size.builtW &&
          wrapper.clientHeight === size.builtH
        )
          return
        buildGrid()
      }, 200)
    })

    document.documentElement.addEventListener('mouseleave', handleMouseLeave)

    buildGrid()
  })
}

onReady(initInfiniteCardsGrid)

/* --------------------------------------------------------------------------
   12. Layered image slider: crossfading backgrounds, sliding titles/masks,
   logo carousel (active centered), autoplay with progress bar, swipe support.
   -------------------------------------------------------------------------- */

function initLayeredImageSlider() {
  document.querySelectorAll('[data-layered-slider-init]').forEach((root) => {
    if (root._layeredSlider) root._layeredSlider.destroy()

    // Collection markup wraps every list in a .w-dyn-list shell and the
    // logos in bare .w-dyn-item shells. Both change the geometry this code
    // measures: percentage heights collapse against the unstyled shell and
    // the logos strip shrinks to nothing once its imgs go absolute. Unwrap
    // back to the manual structure before anything is measured
    root.querySelectorAll('.w-dyn-list').forEach((shell) => {
      const list = shell.firstElementChild
      if (!list || !shell.parentNode) return
      shell.parentNode.insertBefore(list, shell)
      shell.parentNode.removeChild(shell)
    })
    root.querySelectorAll('.w-dyn-item').forEach((wrapper) => {
      // Real slides carry their own class on this element — only bare
      // shells (class list holds w-dyn-item alone) get unwrapped
      if (wrapper.classList.length > 1 || !wrapper.parentNode) return
      while (wrapper.firstChild)
        wrapper.parentNode.insertBefore(wrapper.firstChild, wrapper)
      wrapper.parentNode.removeChild(wrapper)
    })

    const titles = [...root.querySelectorAll('[data-layered-slider-title]')]
    if (!titles.length) {
      // Silence here reads as "the animation broke" — the usual cause is
      // the attribute getting lost in a Webflow rebuild of the block
      if (window.console)
        console.warn(
          '[layered-slider] no [data-layered-slider-title] items found — the block stays static',
          root
        )
      return
    }
    const count = titles.length

    const backgrounds = [...root.querySelectorAll('[data-layered-slider-bg]')]
    const maskItems = [
      ...root.querySelectorAll('[data-layered-slider-mask-item]'),
    ]
    const logos = [...root.querySelectorAll('.layered-slider__logo')]
    const logosTrack = root.querySelector('.layered-slider__logos')
    const maskFrame = root.querySelector('[data-layered-slider-mask]')
    const fill = root.querySelector('[data-layered-slider-fill]')
    const currentEl = root.querySelector('[data-layered-slider-current]')
    const totalEl = root.querySelector('[data-layered-slider-total]')
    const prevBtn = root.querySelector('[data-layered-slider-prev]')
    const nextBtn = root.querySelector('[data-layered-slider-next]')

    // Everything that pauses autoplay on hover
    const controls = [
      ...new Set([...titles, ...logos, ...root.querySelectorAll('a, button')]),
    ]

    // Seconds between slides, 0 disables autoplay. Only a real number turns
    // the default off: a custom attribute left blank in Webflow used to read
    // as NaN and kill the rotation outright
    const autoplayAttr = parseFloat(
      root.getAttribute('data-layered-slider-autoplay')
    )
    const autoplay = Number.isNaN(autoplayAttr) ? 5 : autoplayAttr

    const reduced = matchMedia('(prefers-reduced-motion: reduce)').matches
    const clamp = gsap.utils.clamp
    const wrap = (distance) => distance - count * Math.round(distance / count)

    const transitionDuration = 1
    const backgroundZoom = 0
    const titleGap = 1 // title step = full section width
    const titleSpacing = 40
    const logoGap = 0.35 // logo step, fraction of the strip width
    const logoGapMobile = 0.6 // wider step: neighbours only peek at the edges
    const logoDim = 0.11 // neighbour logo opacity

    if (totalEl) totalEl.textContent = String(count).padStart(2, '0')

    let titleStep = 0
    let maskStep = 0
    let logoStep = 0
    const measure = () => {
      const widestTitle = Math.max(...titles.map((title) => title.offsetWidth))
      titleStep = Math.max(
        root.clientWidth * titleGap,
        widestTitle + titleSpacing
      )
      maskStep = maskFrame ? maskFrame.clientWidth : root.clientWidth
      // On phones the neighbours sit mostly off-screen with just an edge
      // showing (the design look) — the step fraction differs per viewport
      const gapFraction =
        window.matchMedia && matchMedia('(max-width: 479px)').matches
          ? logoGapMobile
          : logoGap
      logoStep = logosTrack ? logosTrack.clientWidth * gapFraction : 0
    }
    measure()

    const state = { progress: 0 }
    let activeIndex = -1

    // Logos are positioned absolutely from the strip center; render() lays
    // them out as a carousel around the active one
    if (logos.length) {
      gsap.set(logos, {
        position: 'absolute',
        left: '50%',
        top: '50%',
        xPercent: -50,
        yPercent: -50,
      })
    }

    // The active slide carries [data-active] on bg, title, mask and logo
    const setActive = (previousIndex, index) => {
      ;[backgrounds, titles, maskItems, logos].forEach((list) => {
        if (previousIndex >= 0 && list[previousIndex])
          list[previousIndex].removeAttribute('data-active')
        if (list[index]) list[index].setAttribute('data-active', '')
      })
    }

    const render = (progress) => {
      const centeredIndex = ((Math.round(progress) % count) + count) % count

      for (let i = 0; i < count; i++) {
        const offset = wrap(i - progress)
        const distance = Math.abs(offset)

        const background = backgrounds[i]
        if (background) {
          const backgroundOpacity = clamp(0, 1, 1 - distance)
          gsap.set(background, {
            opacity: backgroundOpacity,
            scale: 1 + backgroundZoom - backgroundZoom * backgroundOpacity,
            zIndex: Math.round(backgroundOpacity * 100),
          })
        }

        gsap.set(titles[i], {
          x: offset * titleStep,
          opacity: i === centeredIndex ? 1 : 0.4,
          pointerEvents: 'auto',
        })

        const maskItem = maskItems[i]
        if (maskItem) {
          gsap.set(maskItem, { x: offset * maskStep })
        }

        // Active logo bright in the center, neighbours dimmed, further — hidden
        const logo = logos[i]
        if (logo) {
          gsap.set(logo, {
            x: offset * logoStep,
            opacity:
              gsap.utils.interpolate(logoDim, 1, clamp(0, 1, 1 - distance)) *
              clamp(0, 1, 2 - distance),
          })
        }
      }

      if (centeredIndex !== activeIndex) {
        const previousIndex = activeIndex
        activeIndex = centeredIndex
        setActive(previousIndex, centeredIndex)
        if (currentEl)
          currentEl.textContent = String(centeredIndex + 1).padStart(2, '0')
      }
    }

    // The rotation runs only while nothing is hovered AND the section is on
    // screen. Both conditions go through one place, so a state change can
    // never leave the tween paused with no one left to resume it
    let hovering = 0
    let visible = true
    let autoTween = null
    const canPlay = () => hovering === 0 && visible
    const syncAutoplay = () => {
      if (!autoTween) return
      if (canPlay()) autoTween.resume()
      else autoTween.pause()
    }
    const startAutoplay = () => {
      if (!autoTween) return
      // restart(true) counts the delay in: without it a delayedCall fires
      // its callback IMMEDIATELY on restart, and since goTo() restarts
      // autoplay in turn, one swipe cascaded through many slides at once
      autoTween.restart(true)
      if (!canPlay()) autoTween.pause()
    }

    let slideTween = null
    let current = 0
    function goTo(delta) {
      current += delta
      if (slideTween) slideTween.kill()
      slideTween = gsap.to(state, {
        progress: current,
        duration: reduced ? 0 : transitionDuration,
        // CustomEase may be absent from a page; the fallback keeps motion
        ease: window.CustomEase ? 'osmo' : 'power3.inOut',
        onUpdate: () => render(state.progress),
      })
      startAutoplay()
    }

    function goToIndex(i) {
      const delta = wrap(i - current)
      if (delta !== 0) goTo(delta)
    }

    // Autoplay drives the progress bar when the markup has one, and falls
    // back to a bare timer when it does not — the rotation itself must not
    // depend on a decorative element surviving Webflow edits
    if (autoplay > 0 && !reduced) {
      if (fill) {
        gsap.set(fill, { scaleX: 0, transformOrigin: 'left center' })
        autoTween = gsap.to(fill, {
          scaleX: 1,
          duration: autoplay,
          ease: 'none',
          paused: true,
          onComplete: () => goTo(1),
        })
      } else {
        autoTween = gsap.delayedCall(autoplay, () => goTo(1)).pause()
      }
    }

    // Scrolling the section into view is what starts the rotation: a timer
    // running from page load spends its slides on an off-screen block, so
    // by the time it is reached the cycle is already halfway through. With
    // no ScrollTrigger on the page it simply keeps running, as before
    const visibilityTrigger = window.ScrollTrigger
      ? ScrollTrigger.create({
          trigger: root,
          start: 'top bottom',
          end: 'bottom top',
          onToggle: (self) => {
            visible = self.isActive
            syncAutoplay()
          },
        })
      : null
    if (visibilityTrigger) visible = visibilityTrigger.isActive

    // Horizontal swipe navigation — optional: a missing Observer plugin
    // must cost the swipe alone, not the whole slider (an unguarded crash
    // here used to stop init before the first render)
    // One step per gesture: the flag opens only while a real drag is in
    // progress, so a stray pointer move with no press can never slide
    let gestureUsed = true
    const observer =
      window.Observer &&
      Observer.create({
      target: root,
      type: 'touch,pointer',
      dragMinimum: 10,
      tolerance: 25,
      lockAxis: true,
      onDragStart() {
        gestureUsed = false
      },
      onDragEnd() {
        gestureUsed = true
      },
      onLeft(self) {
        if (!gestureUsed && self.isDragging) {
          gestureUsed = true
          goTo(1)
        }
      },
      onRight(self) {
        if (!gestureUsed && self.isDragging) {
          gestureUsed = true
          goTo(-1)
        }
      },
    })

    const onPrev = () => goTo(-1)
    const onNext = () => goTo(1)
    if (prevBtn) prevBtn.addEventListener('click', onPrev)
    if (nextBtn) nextBtn.addEventListener('click', onNext)

    // The active title lets its link through, any other jumps to its slide
    const onTitleClick = (e) => {
      const i = titles.indexOf(e.currentTarget)
      if (i === activeIndex) return
      e.preventDefault()
      goToIndex(i)
    }
    titles.forEach((title) => title.addEventListener('click', onTitleClick))

    const onLogoClick = (e) => {
      const i = logos.indexOf(e.currentTarget)
      if (i >= 0 && i !== activeIndex) goToIndex(i)
    }
    logos.forEach((logo) => logo.addEventListener('click', onLogoClick))

    // Pausing on hover is a mouse idea. On touch, pointerenter fires on tap
    // and its pointerleave may never arrive, which left autoplay paused for
    // good after the first tap on a title or a logo
    const onEnter = (e) => {
      if (e.pointerType && e.pointerType !== 'mouse') return
      hovering++
      syncAutoplay()
    }
    const onLeave = (e) => {
      if (e.pointerType && e.pointerType !== 'mouse') return
      hovering = Math.max(0, hovering - 1)
      syncAutoplay()
    }
    controls.forEach((el) => {
      el.addEventListener('pointerenter', onEnter)
      el.addEventListener('pointerleave', onLeave)
    })

    // Safety net for the counter: a control that moves out from under the
    // cursor (the titles slide sideways on every change) can swallow its
    // pointerleave, and one missed leave freezes the rotation for good
    const onRootLeave = (e) => {
      if (e.pointerType && e.pointerType !== 'mouse') return
      hovering = 0
      syncAutoplay()
    }
    root.addEventListener('pointerleave', onRootLeave)

    const onResize = () => {
      measure()
      render(state.progress)
    }
    window.addEventListener('resize', onResize)
    // Logo images get their final width after load; fonts change title widths
    window.addEventListener('load', onResize)
    if (document.fonts) document.fonts.ready.then(onResize)

    render(0)
    startAutoplay()

    root._layeredSlider = {
      goTo,
      destroy() {
        if (observer) observer.kill()
        if (visibilityTrigger) visibilityTrigger.kill()
        if (slideTween) slideTween.kill()
        if (autoTween) autoTween.kill()
        root.removeEventListener('pointerleave', onRootLeave)
        window.removeEventListener('resize', onResize)
        window.removeEventListener('load', onResize)
        if (prevBtn) prevBtn.removeEventListener('click', onPrev)
        if (nextBtn) nextBtn.removeEventListener('click', onNext)
        titles.forEach((title) =>
          title.removeEventListener('click', onTitleClick)
        )
        logos.forEach((logo) => logo.removeEventListener('click', onLogoClick))
        controls.forEach((el) => {
          el.removeEventListener('pointerenter', onEnter)
          el.removeEventListener('pointerleave', onLeave)
        })
        root._layeredSlider = null
      },
    }
  })
}

onReady(initLayeredImageSlider)

/* --------------------------------------------------------------------------
   13. Cascading slider. Also powers the m-and-a-sell block: the required
   data-cascading-* attributes are applied to its markup at init, and clamp
   mode (no looping + .is-disabled on edge buttons) is enabled for it.
   -------------------------------------------------------------------------- */

function initCascadingSlider() {
  const duration = 0.65
  const ease = 'power3.inOut'

  const breakpoints = [
    { maxWidth: 479, activeWidth: 0.78, siblingWidth: 0.08 },
    { maxWidth: 767, activeWidth: 0.7, siblingWidth: 0.1 },
    { maxWidth: 991, activeWidth: 0.6, siblingWidth: 0.1 },
    // Desktop matches the design at 1316: active 660, siblings 214, far
    // slides land on ~94 through the leftover-width math in measure()
    { maxWidth: Infinity, activeWidth: 0.5, siblingWidth: 0.163 },
  ]

  // Decorate the m-and-a-sell markup so it initializes as a regular instance
  document.querySelectorAll('.m-and-a-sell__body').forEach(function (body) {
    body.setAttribute('data-cascading-slider-wrap', '')
    body.setAttribute('data-cascading-clamp', '')
    body.setAttribute('data-cascading-autoplay', '')
    const items = body.querySelector('.m-and-a-sell__items')
    if (items) items.setAttribute('data-cascading-viewport', '')
    body.querySelectorAll('.m-and-a-sell__item').forEach(function (item) {
      item.setAttribute('data-cascading-slide', '')
    })
    const prev = body.querySelector('.btn-prev')
    const next = body.querySelector('.btn-next')
    if (prev) prev.setAttribute('data-cascading-slider-prev', '')
    if (next) next.setAttribute('data-cascading-slider-next', '')
  })

  const wrappers = document.querySelectorAll('[data-cascading-slider-wrap]')
  wrappers.forEach(setupInstance)

  function setupInstance(wrapper) {
    const viewport = wrapper.querySelector('[data-cascading-viewport]')
    const prevButton = wrapper.querySelector('[data-cascading-slider-prev]')
    const nextButton = wrapper.querySelector('[data-cascading-slider-next]')
    const slides = Array.from(
      viewport.querySelectorAll('[data-cascading-slide]')
    )
    let totalSlides = slides.length

    if (totalSlides === 0) return

    // Clamp mode: bounds are the original (non-clone) slides
    const clampMode = wrapper.hasAttribute('data-cascading-clamp')
    const lastRealIndex = totalSlides - 1

    if (totalSlides < 9) {
      const originalSlides = slides.slice()
      while (slides.length < 9) {
        originalSlides.forEach(function (original) {
          const clone = original.cloneNode(true)
          clone.setAttribute('data-clone', '')
          viewport.appendChild(clone)
          slides.push(clone)
        })
      }
      totalSlides = slides.length
    }

    let activeIndex = 0
    let isAnimating = false
    let slideWidth = 0
    let slotCenters = {}
    let slotWidths = {}

    // Autoplay: advance every 5s; any navigation (manual or auto) restarts
    // the countdown, so a user action never gets an auto-advance right on
    // its heels. In clamp mode the last slide wraps back to the first.
    const autoplayEnabled = wrapper.hasAttribute('data-cascading-autoplay')
    const autoplayDelay = 5000
    let autoplayTimer = null

    function scheduleAutoplay() {
      if (!autoplayEnabled) return
      clearTimeout(autoplayTimer)
      autoplayTimer = setTimeout(function () {
        if (!document.hidden) {
          const nextIndex =
            clampMode && activeIndex >= lastRealIndex ? 0 : activeIndex + 1
          goTo(nextIndex)
        }
        scheduleAutoplay()
      }, autoplayDelay)
    }

    function readGap() {
      const raw = getComputedStyle(viewport).getPropertyValue('--gap').trim()
      if (!raw) return 0
      const temp = document.createElement('div')
      temp.style.width = raw
      temp.style.position = 'absolute'
      temp.style.visibility = 'hidden'
      viewport.appendChild(temp)
      const px = temp.offsetWidth
      viewport.removeChild(temp)
      return px
    }

    function getSettings() {
      const windowWidth = window.innerWidth
      for (let i = 0; i < breakpoints.length; i++) {
        if (windowWidth <= breakpoints[i].maxWidth) return breakpoints[i]
      }
      return breakpoints[breakpoints.length - 1]
    }

    function getOffset(slideIndex, fromIndex) {
      if (fromIndex === undefined) fromIndex = activeIndex
      let distance = slideIndex - fromIndex
      const half = totalSlides / 2
      if (distance > half) distance -= totalSlides
      if (distance < -half) distance += totalSlides
      return distance
    }

    function measure() {
      const settings = getSettings()
      const viewportWidth = viewport.offsetWidth
      const gap = readGap()

      const activeSlideWidth = viewportWidth * settings.activeWidth
      const siblingSlideWidth = viewportWidth * settings.siblingWidth
      const farSlideWidth = Math.max(
        0,
        (viewportWidth - activeSlideWidth - 2 * siblingSlideWidth - 4 * gap) / 2
      )

      slideWidth = activeSlideWidth

      const visibleSlots = [
        { slot: -2, width: farSlideWidth },
        { slot: -1, width: siblingSlideWidth },
        { slot: 0, width: activeSlideWidth },
        { slot: 1, width: siblingSlideWidth },
        { slot: 2, width: farSlideWidth },
      ]

      let x = 0
      visibleSlots.forEach(function (def, i) {
        slotCenters[String(def.slot)] = x + def.width / 2
        slotWidths[String(def.slot)] = def.width
        if (i < visibleSlots.length - 1) x += def.width + gap
      })

      slotCenters['-3'] =
        slotCenters['-2'] - farSlideWidth / 2 - gap - farSlideWidth / 2
      slotWidths['-3'] = farSlideWidth
      slotCenters['3'] =
        slotCenters['2'] + farSlideWidth / 2 + gap + farSlideWidth / 2
      slotWidths['3'] = farSlideWidth

      slides.forEach(function (slide) {
        slide.style.width = slideWidth + 'px'
      })
    }

    function getSlideProps(offset) {
      const clamped = Math.max(-3, Math.min(3, offset))
      const slotWidth = slotWidths[String(clamped)]
      const clipAmount = Math.max(0, (slideWidth - slotWidth) / 2)
      const translateX = slotCenters[String(clamped)] - slideWidth / 2

      return {
        x: translateX,
        '--clip': clipAmount,
        zIndex: 10 - Math.abs(clamped),
      }
    }

    function layout(animate, previousIndex) {
      slides.forEach(function (slide, index) {
        const offset = getOffset(index)

        if (offset < -3 || offset > 3) {
          if (animate && previousIndex !== undefined) {
            const previousOffset = getOffset(index, previousIndex)
            if (previousOffset >= -2 && previousOffset <= 2) {
              const exitSlot = previousOffset < 0 ? -3 : 3
              gsap.to(
                slide,
                Object.assign({}, getSlideProps(exitSlot), {
                  duration: duration,
                  ease: ease,
                  overwrite: true,
                })
              )
              return
            }
          }

          const parkSlot = offset < 0 ? -3 : 3
          gsap.set(slide, getSlideProps(parkSlot))
          return
        }

        const props = getSlideProps(offset)
        slide.setAttribute('data-status', offset === 0 ? 'active' : 'inactive')

        if (animate) {
          gsap.to(
            slide,
            Object.assign({}, props, {
              duration: duration,
              ease: ease,
              overwrite: true,
            })
          )
        } else {
          gsap.set(slide, props)
        }
      })
    }

    function updateButtons() {
      if (!clampMode) return
      if (prevButton)
        prevButton.classList.toggle('is-disabled', activeIndex === 0)
      if (nextButton)
        nextButton.classList.toggle('is-disabled', activeIndex === lastRealIndex)
    }

    function goTo(targetIndex) {
      if (clampMode)
        targetIndex = Math.max(0, Math.min(lastRealIndex, targetIndex))
      const normalizedTarget =
        ((targetIndex % totalSlides) + totalSlides) % totalSlides
      if (isAnimating || normalizedTarget === activeIndex) return
      isAnimating = true

      const previousIndex = activeIndex
      const travelDirection =
        getOffset(normalizedTarget, previousIndex) > 0 ? 1 : -1

      slides.forEach(function (slide, index) {
        const currentOffset = getOffset(index, previousIndex)
        const nextOffset = getOffset(index, normalizedTarget)
        const wasInRange = currentOffset >= -3 && currentOffset <= 3
        const willBeVisible = nextOffset >= -2 && nextOffset <= 2

        if (!wasInRange && willBeVisible) {
          const entrySlot = travelDirection > 0 ? 3 : -3
          gsap.set(slide, getSlideProps(entrySlot))
        }

        const wasInvisible = Math.abs(currentOffset) >= 3
        const willBeStaging = Math.abs(nextOffset) === 3
        const crossesSides = currentOffset * nextOffset < 0
        if (wasInvisible && willBeStaging && crossesSides) {
          gsap.set(slide, getSlideProps(nextOffset > 0 ? 3 : -3))
        }
      })

      activeIndex = normalizedTarget
      updateButtons()
      scheduleAutoplay()
      layout(true, previousIndex)
      gsap.delayedCall(duration + 0.05, function () {
        isAnimating = false
      })
    }

    // preventDefault: buttons may be <a href="#"> links (m-and-a-sell)
    if (prevButton)
      prevButton.addEventListener('click', function (event) {
        event.preventDefault()
        goTo(activeIndex - 1)
      })
    if (nextButton)
      nextButton.addEventListener('click', function (event) {
        event.preventDefault()
        goTo(activeIndex + 1)
      })

    slides.forEach(function (slide, index) {
      slide.addEventListener('click', function () {
        // Clones are out of bounds in clamp mode
        if (clampMode && index > lastRealIndex) return
        if (index !== activeIndex) goTo(index)
      })
    })

    document.addEventListener('keydown', function (event) {
      if (event.key === 'ArrowLeft') goTo(activeIndex - 1)
      if (event.key === 'ArrowRight') goTo(activeIndex + 1)
    })

    // Drag and swipe advance one slide per gesture — mouse and touch. The
    // flag opens only while a real drag is in progress, so a plain pointer
    // move never slides; and the click a browser fires after the gesture
    // is swallowed, or releasing a mouse drag over a side slide would jump
    // once more through the slide's own click handler
    if (window.Observer) {
      var gestureUsed = true
      var swipedAt = 0

      viewport.addEventListener(
        'click',
        function (event) {
          if (Date.now() - swipedAt < 300) {
            event.preventDefault()
            event.stopPropagation()
          }
        },
        true
      )

      // Total travel from the drag start decides the step — direction
      // callbacks depend on per-frame speed and skip slow drags entirely
      var dragStartX = 0

      Observer.create({
        target: viewport,
        type: 'touch,pointer',
        dragMinimum: 10,
        lockAxis: true,
        onDragStart: function (self) {
          gestureUsed = false
          dragStartX = self.x
        },
        onDrag: function (self) {
          if (gestureUsed) return
          var travel = self.x - dragStartX
          if (Math.abs(travel) < 40) return
          gestureUsed = true
          goTo(activeIndex + (travel < 0 ? 1 : -1))
        },
        onDragEnd: function () {
          gestureUsed = true
          swipedAt = Date.now()
        },
      })
    }

    window.addEventListener(
      'resize',
      debounce(function () {
        measure()
        layout(false)
      }, 100)
    )

    measure()
    layout(false)
    updateButtons()
    scheduleAutoplay()
  }
}

onReady(initCascadingSlider)

/* --------------------------------------------------------------------------
   14. Number odometer: scroll-triggered rolling digits.
   Markup: [data-odometer-group] wraps [data-odometer-element]s.
   Stat blocks are wired up here instead of in Webflow (the same approach
   section 13 takes for m-and-a-sell): one group per row, so each row rolls
   as it reaches the viewport rather than all of them at the first one.
   -------------------------------------------------------------------------- */

function initNumberOdometer() {
  const prefersReducedMotion = window.matchMedia(
    '(prefers-reduced-motion: reduce)'
  ).matches
  const initFlag = 'data-odometer-initialized'
  const activeTweens = new WeakMap()

  // Roll feel: power3.out threw the digits forward and stopped them dead.
  // power2.inOut spins up and settles gently at both ends, and the longer
  // duration plus a wider digit stagger lets the cascade read digit by
  // digit instead of arriving as one snap
  const defaults = {
    duration: 1.6,
    ease: 'power2.inOut',
    elementStagger: 0.12,
    digitStagger: 0.07,
    revealDuration: 0.5,
    revealEase: 'power2.out',
    triggerStart: 'top 80%',
    staggerOrder: 'left',
    digitCycles: 2,
  }

  // Numbers carry their suffix in the same string ("300+", "25 +"): the
  // parser rolls the digits and leaves everything else standing
  document.querySelectorAll('.stat-intro-stat__row').forEach((row) => {
    const numbers = row.querySelectorAll('.h2-regular')
    if (!numbers.length) return
    row.setAttribute('data-odometer-group', '')
    numbers.forEach((el) => el.setAttribute('data-odometer-element', ''))
  })

  document.querySelectorAll('[data-odometer-group]').forEach((group) => {
    if (group.hasAttribute(initFlag)) return
    group.setAttribute(initFlag, '')

    const elements = Array.from(
      group.querySelectorAll('[data-odometer-element]')
    )
    if (!elements.length || prefersReducedMotion) return

    const staggerOrder =
      group.getAttribute('data-odometer-stagger-order') || defaults.staggerOrder
    const triggerStart =
      group.getAttribute('data-odometer-trigger-start') || defaults.triggerStart
    const elementStagger =
      parseFloat(group.getAttribute('data-odometer-stagger')) ||
      defaults.elementStagger

    const elementData = elements.map((el) => {
      const originalText = el.textContent.trim()
      const hasExplicitStart = el.hasAttribute('data-odometer-start')
      const startValue = parseFloat(el.getAttribute('data-odometer-start')) || 0
      const duration =
        parseFloat(el.getAttribute('data-odometer-duration')) ||
        defaults.duration
      const step = getLineHeightRatio(el)

      let segments = parseSegments(originalText)
      segments = mapStartDigits(segments, startValue)
      segments = markHiddenSegments(segments, startValue)

      const grow = shouldGrow(el, hasExplicitStart, startValue, segments)
      const { rollers, revealEls } = buildRollerDOM(el, segments, step, grow)

      const fontSize = parseFloat(getComputedStyle(el).fontSize)
      const revealData = revealEls.map((revealEl) => {
        const widthEm = revealEl.offsetWidth / fontSize
        gsap.set(revealEl, { width: 0, overflow: 'hidden' })
        return { el: revealEl, widthEm }
      })

      return { el, rollers, duration, step, revealData, originalText }
    })

    const ordered = applyStaggerOrder(elementData, staggerOrder)

    const tl = gsap.timeline({
      scrollTrigger: {
        trigger: group,
        start: triggerStart,
        // no `once`: see section 30 — a finished timeline won't replay
      },
      onComplete() {
        elementData.forEach(({ el, originalText }) => {
          cleanupElement(el, originalText)
        })
      },
    })

    ordered.forEach((data, orderIdx) => {
      const { rollers, duration, step, revealData } = data
      const offset = orderIdx * elementStagger

      revealData.forEach(({ el, widthEm }) => {
        tl.to(
          el,
          {
            width: widthEm + 'em',
            opacity: 1,
            duration: defaults.revealDuration,
            ease: defaults.revealEase,
          },
          offset
        )
      })

      rollers.forEach(({ roller, targetPos }, digitIdx) => {
        const reversedIdx = rollers.length - 1 - digitIdx
        tl.to(
          roller,
          {
            y: -targetPos * step + 'em',
            duration,
            ease: defaults.ease,
            force3D: true,
          },
          offset + reversedIdx * defaults.digitStagger
        )
      })
    })
  })

  // Programmatic update (optional add-on)
  return function updateOdometer(el, newText, options = {}) {
    const currentText = el.textContent.trim()
    if (currentText === newText) return

    const duration = options.duration || defaults.duration
    const ease = options.ease || defaults.ease
    const step = getLineHeightRatio(el)

    const existing = activeTweens.get(el)
    if (existing) {
      existing.kill()
      gsap.set(el, { clearProps: 'width,overflow' })
    }

    // Measure in em so it scales responsively
    const fontSize = parseFloat(getComputedStyle(el).fontSize)
    const oldWidthEm = el.getBoundingClientRect().width / fontSize

    const startSegments = parseSegments(currentText)
    const startDigitsStr = startSegments
      .filter((s) => s.type === 'digit')
      .map((s) => s.char)
      .join('')
    const startValue = parseInt(startDigitsStr, 10) || 0

    let segments = parseSegments(newText)
    segments = mapStartDigits(segments, startValue)
    segments = markHiddenSegments(segments, startValue)
    const { rollers, revealEls } = buildRollerDOM(el, segments, step, true)

    const newWidthEm = el.getBoundingClientRect().width / fontSize
    const widthChanged = Math.abs(oldWidthEm - newWidthEm) > 0.01

    if (widthChanged) {
      gsap.set(el, { width: oldWidthEm + 'em', overflow: 'hidden' })
    }

    const tl = gsap.timeline({
      onComplete() {
        cleanupElement(el, newText)
        activeTweens.delete(el)
      },
    })
    activeTweens.set(el, tl)

    if (widthChanged) {
      tl.to(
        el,
        {
          width: newWidthEm + 'em',
          duration: defaults.revealDuration,
          ease: defaults.revealEase,
        },
        0
      )
    }

    revealEls.forEach((revealEl) => {
      if (revealEl.getAttribute('data-odometer-part') === 'static') {
        tl.to(revealEl, { opacity: 1, duration: 0.2 }, 0)
      }
    })

    rollers.forEach(({ roller, targetPos }, digitIdx) => {
      const reversedIdx = rollers.length - 1 - digitIdx
      tl.to(
        roller,
        {
          y: -targetPos * step + 'em',
          duration,
          ease,
          force3D: true,
        },
        reversedIdx * defaults.digitStagger
      )
    })
  }

  function getLineHeightRatio(el) {
    const cs = getComputedStyle(el)
    const lh = cs.lineHeight
    if (lh === 'normal') return 1.2
    return parseFloat(lh) / parseFloat(cs.fontSize)
  }

  function parseSegments(text) {
    return [...text].map((char) => ({
      type: /\d/.test(char) ? 'digit' : 'static',
      char,
    }))
  }

  function mapStartDigits(segments, startValue) {
    const digitSlots = segments.filter((s) => s.type === 'digit')
    const padded = String(Math.floor(Math.abs(startValue)))
      .padStart(digitSlots.length, '0')
      .slice(-digitSlots.length)
    let di = 0
    return segments.map((s) =>
      s.type === 'digit' ? { ...s, startDigit: parseInt(padded[di++], 10) } : s
    )
  }

  function markHiddenSegments(segments, startValue) {
    const totalDigits = segments.filter((s) => s.type === 'digit').length
    const absStart = Math.floor(Math.abs(startValue))
    const startDigitCount = absStart === 0 ? 1 : String(absStart).length
    const leadingZeros = Math.max(0, totalDigits - startDigitCount)
    if (leadingZeros === 0) return segments
    let digitsSeen = 0
    let firstDigitSeen = false
    let prevDigitHidden = false
    return segments.map((seg) => {
      if (seg.type === 'digit') {
        firstDigitSeen = true
        const hidden = digitsSeen < leadingZeros
        prevDigitHidden = hidden
        digitsSeen++
        return { ...seg, hidden }
      }
      const hidden = firstDigitSeen && prevDigitHidden
      return { ...seg, hidden }
    })
  }

  function shouldGrow(el, hasExplicitStart, startValue, segments) {
    if (el.hasAttribute('data-odometer-grow')) {
      return el.getAttribute('data-odometer-grow') !== 'false'
    }
    if (!hasExplicitStart) return false
    const absStart = Math.floor(Math.abs(startValue))
    const startDigitCount = absStart === 0 ? 1 : String(absStart).length
    const endDigitCount = segments.filter((s) => s.type === 'digit').length
    return startDigitCount < endDigitCount
  }

  function buildRollerDOM(el, segments, step, grow) {
    el.innerHTML = ''
    el.style.height = ''
    const rollers = []
    const revealEls = []
    const totalCells = 10 * defaults.digitCycles
    segments.forEach((seg) => {
      if (seg.type === 'static') {
        const span = document.createElement('span')
        span.setAttribute('data-odometer-part', 'static')
        span.style.height = step + 'em'
        span.style.lineHeight = step
        span.textContent = seg.char
        el.appendChild(span)
        if (grow && seg.hidden) {
          gsap.set(span, { opacity: 0 })
          revealEls.push(span)
        }
        return
      }
      const mask = document.createElement('span')
      mask.setAttribute('data-odometer-part', 'mask')
      mask.style.height = step + 'em'
      mask.style.lineHeight = step
      const roller = document.createElement('span')
      roller.setAttribute('data-odometer-part', 'roller')
      roller.style.lineHeight = step

      const digits = []
      for (let d = 0; d < totalCells; d++) {
        digits.push(d % 10)
      }
      roller.textContent = digits.join('\n')
      mask.appendChild(roller)
      el.appendChild(mask)
      const startDigit = seg.startDigit || 0
      const isReveal = grow && seg.hidden
      gsap.set(roller, {
        y: isReveal ? step + 'em' : -startDigit * step + 'em',
      })
      const endDigit = parseInt(seg.char, 10)
      const targetPos = endDigit > startDigit ? endDigit : 10 + endDigit
      rollers.push({ roller, targetPos })
      if (isReveal) revealEls.push(mask)
    })
    return { rollers, revealEls }
  }

  function cleanupElement(el, originalText) {
    el.style.overflow = ''
    el.style.height = ''

    // Replace rollers with the final digits, keep the width
    const digits = [...originalText].filter((c) => /\d/.test(c))
    let di = 0

    el.querySelectorAll('[data-odometer-part="mask"]').forEach((mask) => {
      const roller = mask.querySelector('[data-odometer-part="roller"]')
      if (roller) roller.remove()
      mask.textContent = digits[di++] || ''
      mask.style.opacity = ''
      mask.style.overflow = ''
    })

    el.querySelectorAll('[data-odometer-part="static"]').forEach((stat) => {
      stat.style.opacity = ''
    })
  }

  function recalcOnResize() {
    document.querySelectorAll('[data-odometer-element]').forEach((el) => {
      const running = activeTweens.get(el)
      if (running) {
        running.progress(1)
        activeTweens.delete(el)
      }

      const hasRollers = el.querySelector('[data-odometer-part="roller"]')

      if (hasRollers) {
        const step = getLineHeightRatio(el)
        el.querySelectorAll('[data-odometer-part="mask"]').forEach((mask) => {
          mask.style.height = step + 'em'
          mask.style.lineHeight = step
        })
        el.querySelectorAll('[data-odometer-part="roller"]').forEach(
          (roller) => {
            roller.style.lineHeight = step
          }
        )
        el.querySelectorAll('[data-odometer-part="static"]').forEach((stat) => {
          stat.style.lineHeight = step
        })
      }
    })
    ScrollTrigger.refresh()
  }

  let resizeTimer
  let lastWidth = window.innerWidth
  window.addEventListener('resize', () => {
    clearTimeout(resizeTimer)
    resizeTimer = setTimeout(() => {
      if (window.innerWidth === lastWidth) return
      lastWidth = window.innerWidth
      recalcOnResize()
    }, 250)
  })

  function applyStaggerOrder(items, order) {
    const arr = [...items]
    if (order === 'right') return arr.reverse()
    if (order === 'random') return shuffleArray(arr)
    return arr
  }

  function shuffleArray(arr) {
    for (let i = arr.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1))
      ;[arr[i], arr[j]] = [arr[j], arr[i]]
    }
    return arr
  }
}

onReady(initNumberOdometer)

/* --------------------------------------------------------------------------
   15. ScrollTrigger refresh-order fix. Triggers created early refresh in
   creation order and miss offsets that settle late (fonts, SplitText,
   lazy images) — so they can fire too early. sort() orders them by page
   position, refresh() recalculates. Runs on load, when layout is
   nominally final — and again whenever late content actually moves the
   layout: lazy images land DURING scrolling (cold cache especially) and
   push everything below them down, leaving triggers with stale, higher
   starts — sections arrived "already played", or never fired at all.
   Every image that is not yet complete queues a debounced resync on
   load, and a ResizeObserver on <body> catches any other height change
   (CMS embeds, odometers, whatever). The debounce groups image bursts
   so one settle costs one refresh.
   -------------------------------------------------------------------------- */

;(function () {
  function resync() {
    if (!window.gsap || !window.ScrollTrigger) return
    ScrollTrigger.sort()
    ScrollTrigger.refresh()
  }

  var timer
  function queueResync() {
    clearTimeout(timer)
    timer = setTimeout(resync, 200)
  }

  window.addEventListener('load', resync)
  // Webfonts can land after load and reflow every heading below the fold —
  // stale trigger positions made reveals fire late or not at all
  if (document.fonts && document.fonts.ready) document.fonts.ready.then(resync)

  onReady(function () {
    toArray('img').forEach(function (img) {
      if (img.complete) return
      img.addEventListener('load', queueResync, { once: true })
      img.addEventListener('error', queueResync, { once: true })
    })

    // Guarded by a height delta: refresh itself can nudge layout by a
    // fraction and must not feed back into another refresh
    if (window.ResizeObserver) {
      var lastHeight = document.body.scrollHeight
      new ResizeObserver(function () {
        var height = document.body.scrollHeight
        if (Math.abs(height - lastHeight) < 2) return
        lastHeight = height
        queueResync()
      }).observe(document.body)
    }
  })
})()

/* --------------------------------------------------------------------------
   16. Hits grid: expose each row's item count as .hits__row--count-N so CSS
   can handle partially filled rows. Counting in CSS is unreliable here —
   the last child of a row is .hits__dec-top, not an item.
   A collection-driven list arrives flat instead: one w-dyn-items row with
   every hit inside. It is rebuilt into the manual structure first — rows
   of ROW_SIZE, each closed with its full-bleed .hits__dec-top — so the
   border and decoration CSS keeps working unchanged.
   -------------------------------------------------------------------------- */

;(function () {
  var ROW_SIZE = 8 // items per authored row: two grid lines of four

  onReady(function () {
    toArray('.hits__items').forEach(function (list) {
      var flat = list.querySelector('.hits__row.w-dyn-items')
      if (!flat) return

      var items = toArray('.hits-item', flat)
      if (!items.length) return

      for (var i = 0; i < items.length; i += ROW_SIZE) {
        var row = document.createElement('div')
        row.className = 'hits__row'
        items.slice(i, i + ROW_SIZE).forEach(function (item) {
          row.appendChild(item)
        })
        var dec = document.createElement('div')
        dec.className = 'hits__dec-top'
        row.appendChild(dec)
        list.insertBefore(row, flat)
      }
      list.removeChild(flat)
    })

    toArray('.hits__row').forEach(function (row) {
      var count = row.querySelectorAll('.hits-item').length
      if (count) row.classList.add('hits__row--count-' + count)
    })
  })
})()

/* --------------------------------------------------------------------------
   17. Form fields: floating labels + character counter.
   .inp-group gets .is-focused / .is-filled, which CSS uses to float the
   placeholder above the field. Class-based rather than :placeholder-shown —
   the fields ship with an empty placeholder attribute, which that
   pseudo-class does not match reliably.
   -------------------------------------------------------------------------- */

;(function () {
  onReady(function () {
    toArray('.inp-group').forEach(function (group) {
      var field = group.querySelector('.inp-group__input, .inp-group__textarea')
      if (!field) return

      // Counter runs only for groups that have a status element.
      // maxlength on the field stays the single source of truth for the limit
      var status = group.querySelector('.inp-group__status')
      var limit = parseInt(field.getAttribute('maxlength'), 10) || 0

      function syncFilled() {
        group.classList.toggle('is-filled', field.value.trim() !== '')

        if (status && limit) {
          status.textContent = field.value.length + '/' + limit
          group.classList.toggle('is-limit', field.value.length >= limit)
        }
      }

      field.addEventListener('focus', function () {
        group.classList.add('is-focused')
      })
      field.addEventListener('blur', function () {
        group.classList.remove('is-focused')
        syncFilled()
      })
      field.addEventListener('input', syncFilled)
      field.addEventListener('change', syncFilled)

      syncFilled() // values restored by the browser on reload
      window.addEventListener('load', syncFilled) // autofill lands later
    })
  })
})()

/* --------------------------------------------------------------------------
   18. Contact form: validation + submit to the Webflow forms endpoint.
   Unlike the previous project, .form here IS the native Webflow form — it
   carries data-wf-page-id / data-wf-element-id and holds the turnstile input,
   so all ids and the token are read straight off it.
   -------------------------------------------------------------------------- */

;(function () {
  if (!window.jQuery) return
  var $ = window.jQuery

  // Success copy lives outside the form (modal header), so [data-form-success]
  // elements are looked up in the nearest modal rather than inside the form
  var SUCCESS_SCOPE = '.modal'

  // The red error highlight is not permanent: it clears itself this long
  // after a failed attempt (another submit restarts the countdown)
  var ERROR_TTL = 5000

  function clearErrors($form) {
    $form.find('.inp-group, .checkbox').removeClass('is-error')
  }

  function scheduleErrorClear($form) {
    clearTimeout($form.data('errorTimer'))
    $form.data(
      'errorTimer',
      setTimeout(function () {
        clearErrors($form)
      }, ERROR_TTL)
    )
  }

  function successCopyEls($form) {
    var $scope = $form.closest(SUCCESS_SCOPE)
    if (!$scope.length) $scope = $(document)
    return $scope.find('[data-form-success]')
  }

  function showSuccessState($form) {
    $form.addClass('is-success-sent')
    $form.find('.form__success').addClass('is-active')

    // Several elements may carry the same success text (the modal details
    // row: position, "•" and location all do) — it must not repeat, so the
    // first of them shows it and the duplicates hide until reset
    var seen = []

    successCopyEls($form).each(function () {
      var $el = $(this)
      // Keep the original markup so "send another" can restore it
      if ($el.data('formCopy') === undefined) $el.data('formCopy', $el.html())

      var text = $el.attr('data-form-success')
      if (seen.indexOf(text) === -1) {
        seen.push(text)
        $el.text(text)
      } else {
        $el.css('display', 'none')
      }
    })
  }

  function resetSuccessState($form) {
    $form.removeClass('is-success-sent')
    $form.find('.form__success').removeClass('is-active')

    successCopyEls($form).each(function () {
      var original = $(this).data('formCopy')
      if (original !== undefined) $(this).html(original)
      $(this).css('display', '') // duplicates hidden by showSuccessState
    })
  }

  function validateEmail(email) {
    var re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    return re.test(email)
  }

  function validateForm($form) {
    var isValid = true

    $form.find('.inp-group').removeClass('is-error')

    $form.find('input[required], textarea[required]').each(function () {
      var $input = $(this)
      var $group = $input.closest('.inp-group')
      var value = $input.val().trim()
      var type = $input.attr('type')

      if (!value) {
        $group.addClass('is-error')
        isValid = false
      } else if (type === 'email' && !validateEmail(value)) {
        $group.addClass('is-error')
        isValid = false
      }
    })

    // Terms checkbox has to be ticked before sending
    var $agree = $form.find('.checkbox__input[type="checkbox"]')
    if ($agree.length) {
      var agreed = $agree.is(':checked')
      $agree.closest('.checkbox').toggleClass('is-error', !agreed)
      if (!agreed) isValid = false
    }

    if (isValid) clearTimeout($form.data('errorTimer'))
    else scheduleErrorClear($form)

    return isValid
  }

  function sendForm($form) {
    var endPointId = $('html').attr('data-wf-site')
    var pageId = $form.attr('data-wf-page-id') || $('html').attr('data-wf-page')
    var elementId = $form.attr('data-wf-element-id') || ''
    var domain = window.location.hostname
    var url = 'https://webflow.com/api/v1/form/' + endPointId

    var formName = $form.attr('data-name') || $form.attr('name') || 'Contact'

    var data = new URLSearchParams()
    data.append('name', formName)
    data.append('source', window.location.href)
    data.append('test', 'false')
    data.append('dolphin', 'false')
    if (pageId) data.append('pageId', pageId)
    if (elementId) data.append('elementId', elementId)
    data.append('domain', domain)

    $form.find('input, textarea, select').each(function () {
      var $input = $(this)
      var name = $input.attr('name')
      var type = $input.attr('type')

      // Turnstile is appended separately below
      if (name && name !== 'cf-turnstile-response') {
        if (type === 'checkbox') {
          data.append(
            'fields[' + name + ']',
            $input.is(':checked') ? 'true' : 'false'
          )
        } else if (type === 'radio') {
          if ($input.is(':checked')) {
            data.append('fields[' + name + ']', $input.val())
          }
        } else {
          data.append('fields[' + name + ']', $input.val().trim())
        }
      }
    })

    var turnstileResponse = $form
      .find('input[name="cf-turnstile-response"]')
      .val()
    if (turnstileResponse) {
      data.append('fields[cf-turnstile-response]', turnstileResponse)
    }

    data.append('fields[submit_url]', window.location.href)

    $form.addClass('is-sending')

    // Success shows as soon as EITHER delivery lands: Webflow keeps the
    // submissions journal, the worker (team form only) e-mails the member
    // through SendGrid. Only both failing leaves the form un-sent
    var settled = false
    var failures = 0
    var attempts = 1

    function onSuccess() {
      if (settled) return
      settled = true
      showSuccessState($form)
      $form[0].reset()
      $form.removeClass('is-sending')
      // reset() empties the fields, so let section 17 re-sync labels and the
      // counter. Native dispatch: jQuery .trigger() never reaches handlers
      // registered with addEventListener
      $form.find('.inp-group__input, .inp-group__textarea').each(function () {
        this.dispatchEvent(new Event('input', { bubbles: true }))
      })
      $form.find('.inp-group').removeClass('is-focused')
    }

    function onFailure() {
      failures++
      if (!settled && failures >= attempts) $form.removeClass('is-sending')
    }

    var workerRequest = sendToWorker($form)
    if (workerRequest) {
      attempts++
      workerRequest.then(onSuccess, function (reason) {
        if (window.console)
          console.warn('[contact] worker delivery failed', reason)
        onFailure()
      })
    }

    $.ajax({
      url: url,
      method: 'POST',
      contentType: 'application/x-www-form-urlencoded',
      data: data.toString(),
      success: onSuccess,
      error: onFailure,
    })
  }

  // Team form only: the hidden team-member-email field (filled by section
  // 9 from the pinned card) routes the message to a Cloudflare Worker that
  // sends it to the member through SendGrid. Forms without the field — the
  // general contact form — return null and stay Webflow-only
  var WORKER_URL = 'https://mh-contact.jaclyn-757.workers.dev'

  function sendToWorker($form) {
    var memberEmail = $form.find('input[name="team-member-email"]').val()
    if (!memberEmail || !window.fetch) return null

    return fetch(WORKER_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        firstName: $form.find('input[name="first-name"]').val() || '',
        lastName: $form.find('input[name="last-name"]').val() || '',
        email: $form.find('input[name="email"]').val() || '',
        message: $form.find('textarea[name="tell"]').val() || '',
        memberName: $form.find('input[name="team-member-name"]').val() || '',
        memberEmail: memberEmail,
      }),
    }).then(function (response) {
      if (!response.ok) throw new Error('worker responded ' + response.status)
      return response
    })
  }

  onReady(function () {
    $('.form').on('submit', function (e) {
      e.preventDefault()

      var $form = $(this)

      if (!validateForm($form)) return

      sendForm($form)
    })

    // The visible submit button is an <a>, not a submit input. Scoped to
    // .form__controls so it does not catch the "send another" button, which
    // sits inside the same form with the same class
    $('.form .form__controls .btn-black').on('click', function (e) {
      e.preventDefault()

      var $form = $(this).closest('.form')

      if (!validateForm($form)) return

      sendForm($form)
    })

    // "Send another message" — back to the empty form and the original copy
    $('.form .form__success .btn-black, [data-form-reset]').on(
      'click',
      function (e) {
        e.preventDefault()
        resetSuccessState($(this).closest('.form'))
      }
    )
  })
})()

/* --------------------------------------------------------------------------
   19. FAQ accordion: .faq-item__head toggles its item. The open state is the
   .is-active class — CSS turns the plus icon 45° into a cross and reveals a
   body flagged in the markup; the slide itself is jQuery. Single-open:
   expanding an item folds the previously active one.
   -------------------------------------------------------------------------- */

;(function () {
  if (!window.jQuery) return
  var $ = window.jQuery

  var DURATION = 400

  onReady(function () {
    var $items = $('.faq-item')
    if (!$items.length) return

    // Items flagged .is-active in the Webflow markup start open. Inline
    // show() rather than a CSS rule: the slide-up must survive the class
    // being removed at the very start of the closing click
    $items.filter('.is-active').children('.faq-item__body').show()

    $items.find('.faq-item__head').on('click', function () {
      var $item = $(this).closest('.faq-item')
      var $body = $item.children('.faq-item__body')

      // The class decides, the slide follows — rapid clicking can never
      // drive the icon and the body out of sync
      var opened = !$item.hasClass('is-active')

      // Single-open: fold whatever else is expanded before revealing this one
      if (opened) {
        var $others = $items.filter('.is-active').not($item)
        $others.removeClass('is-active')
        $others.children('.faq-item__body').stop(true).slideUp(DURATION)
      }

      $item.toggleClass('is-active', opened)

      if (opened) $body.stop(true).slideDown(DURATION)
      else $body.stop(true).slideUp(DURATION)
    })
  })
})()

/* --------------------------------------------------------------------------
   20. Football clients: the team-grid pattern on the players. Cards rest at
   full opacity; hovering one dims the rest and grows its photo OUT of the
   card, above the neighbours (z-index only ever increases, as in the team
   grid). Click pins the card in that state and opens #modal-person-details
   through the modal system (the data-modal-show attribute is added here,
   not in Webflow), filled from the card content and data-modal-person-*
   attributes; ONLY attribute-borne fields stub from the first item when an
   item's own attribute is empty. The pin releases when the modal closes.
   The card's arrow icon (.football-clients-item__icon, inside __visual)
   grows in with the photo, parked in the zoomed photo's top-right corner
   ICON_INSET from both edges and scaled around the photo center — the same
   trick as the team grid (section 9). Its spot is computed on every zoom
   from the photo's layout box, so lazy-loaded images and resizes need no
   bookkeeping.
   -------------------------------------------------------------------------- */

;(function () {
  var DIM = 0.4
  var SCALE = 1.5 // the photo grows out of the card, like the team grid
  var DURATION = 0.4 // same for opacity and scale so they finish together
  var ICON_INSET_REM = 1.25 // arrow icon offset from the zoomed photo's top/right
  var PERSON_MODAL = '#modal-person-details'

  onReady(function () {
    if (!window.gsap) return

    var items = toArray('.football-clients-item')
    if (!items.length) return

    gsap.set(items, { position: 'relative', zIndex: 1 })

    var zCounter = 1

    // The card pinned by the open modal: it keeps the zoomed state and the
    // grid stays inert until the modal closes
    var active = null

    function photoOf(item) {
      return item.querySelector('.football-clients-item__illustration')
    }

    function iconOf(item) {
      return item.querySelector('.football-clients-item__icon')
    }

    // Park the icon where the ZOOMED photo's top-right corner will be, and
    // pivot its scale on the photo center. Layout (offset*) values are used
    // on purpose: they ignore the photo's transform
    function placeIcon(item) {
      var icon = iconOf(item)
      var photo = photoOf(item)
      if (!icon || !photo || !photo.offsetWidth) return
      var inset =
        ICON_INSET_REM *
        parseFloat(getComputedStyle(document.documentElement).fontSize)
      var cx = photo.offsetLeft + photo.offsetWidth / 2
      var cy = photo.offsetTop + photo.offsetHeight / 2
      var top = cy - (photo.offsetHeight * SCALE) / 2 + inset
      var left = cx + (photo.offsetWidth * SCALE) / 2 - inset - icon.offsetWidth
      gsap.set(icon, {
        top: top + 'px',
        left: left + 'px',
        right: 'auto',
        bottom: 'auto',
        transformOrigin: cx - left + 'px ' + (cy - top) + 'px',
      })
    }

    // Collapsed state for every icon; the position comes with the first zoom
    items.forEach(function (item) {
      var icon = iconOf(item)
      if (icon) gsap.set(icon, { scale: 1 / SCALE, autoAlpha: 0 })
    })

    // Hovered/pinned card: full opacity and grown photo, the rest dim
    function zoom(item) {
      gsap.set(item, { zIndex: ++zCounter })
      items.forEach(function (other) {
        gsap.to(other, {
          opacity: other === item ? 1 : DIM,
          duration: DURATION,
          ease: 'power2.out',
          overwrite: 'auto',
        })
      })
      var photo = photoOf(item)
      if (photo)
        gsap.to(photo, {
          scale: SCALE,
          duration: DURATION,
          ease: 'power2.out',
          overwrite: 'auto',
        })
      var icon = iconOf(item)
      if (icon) {
        placeIcon(item)
        gsap.to(icon, {
          scale: 1,
          autoAlpha: 1,
          duration: DURATION,
          ease: 'power2.out',
          overwrite: 'auto',
        })
      }
    }

    // Everything back: full opacity everywhere, the photo into its frame
    function unzoom(item) {
      gsap.to(items, {
        opacity: 1,
        duration: DURATION,
        ease: 'power2.out',
        overwrite: 'auto',
      })
      var photo = photoOf(item)
      if (photo)
        gsap.to(photo, {
          scale: 1,
          duration: DURATION,
          ease: 'power2.out',
          overwrite: 'auto',
        })
      var icon = iconOf(item)
      if (icon)
        gsap.to(icon, {
          scale: 1 / SCALE,
          autoAlpha: 0,
          duration: DURATION,
          ease: 'power2.out',
          overwrite: 'auto',
        })
    }

    // Touch gets neither the dim nor the zoom: the 1.5 upscale is exactly
    // what reads as a "blurred" photo on phones, and a tap only needs to
    // open the modal
    function canHover() {
      return !(window.matchMedia && matchMedia('(hover: none)').matches)
    }

    items.forEach(function (item) {
      item.addEventListener('mouseenter', function () {
        if (!canHover()) return
        if (active) return // modal pinned a card: the grid is inert
        zoom(item)
      })
      item.addEventListener('mouseleave', function () {
        if (!canHover()) return
        if (active) return
        unzoom(item)
      })
    })

    var modal = document.querySelector(PERSON_MODAL)
    if (!modal) return

    // The person modal names its head blocks person-head__* (the contact
    // modal's modal__* naming is kept as a fallback). The head is scoped so
    // the .body-l lookup never leaks into the modal footer
    var headVisual = modal.querySelector('.person-head__visual')
    var modalHead =
      modal.querySelector('.person-head') ||
      (headVisual && headVisual.parentElement) ||
      modal.querySelector('.modal__head') ||
      modal
    var modalPhoto = modal.querySelector('.person-head__photo, .modal__photo')
    var modalName = modalHead.querySelector('.h4-italic')
    var modalDetails = modalHead.querySelectorAll('.body-l')
    var personBody = modal.querySelector('.person-body')
    var personTags = modal.querySelector('.person-body__tags')
    var personDescription = modal.querySelector(
      '.person-body__description .body-l'
    )

    // Opening goes through the modal system — section 4 delegates opener
    // clicks, so the attribute is the only wiring the cards need
    items.forEach(function (item) {
      item.setAttribute('data-modal-show', PERSON_MODAL)
    })

    function cardText(item, selector) {
      var el = item.querySelector(selector)
      return el ? el.textContent.trim() : ''
    }

    // Attribute-only fields: own value, else the first item's — the agreed
    // stub while the CMS attributes are still empty. Card-borne fields
    // (name, team) never stub: every card carries its own
    function attrOrStub(item, name) {
      return item.getAttribute(name) || items[0].getAttribute(name) || ''
    }

    // The card's .body-m holds team and role in ONE string ("Chicago Bears
    // Quarterback"), and some cards carry the team alone ("Denver Broncos").
    // The modal separates the two around the "•", so the role is peeled off
    // the end of the string by this list. Longest first, matched as a whole
    // word; a role the list misses simply stays glued to the team (no "•")
    var ROLES = [
      'Wide Receiver',
      'Running Back',
      'Offensive Line',
      'Defensive Line',
      'Deffensive Line', // sic — the CMS spells it this way
      'Tight End',
      'Tightend',
      'Quarterback',
      'Linebacker',
      'Cornerback',
      'Safety',
      'Center',
      'Guard',
      'Tackle',
      'Edge',
      'Kicker',
      'Punter',
    ]

    function splitTeamRole(text) {
      for (var i = 0; i < ROLES.length; i++) {
        var match = text.match(
          new RegExp('^(.*\\S)\\s+(' + ROLES[i] + ')\\s*$', 'i')
        )
        if (match) return { team: match[1], role: match[2] }
      }
      return { team: text, role: '' }
    }

    function fillPersonModal(item) {
      // Same dance as the contact modal: the copy is line-split by section 4
      // and cached on the element — revert that BEFORE swapping the text,
      // so the open animation splits the fresh content
      if (modal._splits) {
        modal._splits.forEach(function (s) {
          s.revert()
        })
        modal._splits = null
      }
      if (modal._lineTl) {
        modal._lineTl.kill()
        modal._lineTl = null
      }

      var photo = photoOf(item)

      if (modalPhoto && photo) modalPhoto.src = photo.currentSrc || photo.src
      if (modalName) {
        modalName.textContent =
          item.getAttribute('data-modal-person-name') ||
          cardText(item, '.body-l')
        fitText(modalName)
      }

      // Subtitle: a card carrying data-modal-additional-title passes it
      // through as-is — one ready-made line, no divider games. Cards
      // without the attribute keep the legacy team/role split below:
      // [0] team, [1] the "•" divider, [2] role out of the card's .body-m
      // via splitTeamRole, attributes overriding either one. The divider
      // only makes sense between two values, so it hides together with an
      // empty side ("Denver Broncos" stays alone)
      var additional = item.getAttribute('data-modal-additional-title')
      if (additional !== null) {
        if (modalDetails[0]) {
          modalDetails[0].textContent = additional
          modalDetails[0].style.display = additional ? '' : 'none'
        }
        if (modalDetails[1]) modalDetails[1].style.display = 'none'
        if (modalDetails[2]) modalDetails[2].style.display = 'none'
      } else {
        var meta = splitTeamRole(cardText(item, '.body-m'))
        var team = item.getAttribute('data-modal-person-team') || meta.team
        var role =
          item.getAttribute('data-modal-person-position') ||
          meta.role ||
          attrOrStub(item, 'data-modal-person-location')

        if (modalDetails[0]) {
          modalDetails[0].textContent = team
          modalDetails[0].style.display = team ? '' : 'none'
        }
        if (modalDetails[2]) {
          modalDetails[2].textContent = role
          modalDetails[2].style.display = role ? '' : 'none'
        }
        if (modalDetails[1])
          modalDetails[1].style.display = team && role ? '' : 'none'
      }

      // Tags and description belong to the card as well: comma-separated
      // data-modal-tags and data-modal-description. A card with neither
      // hides the whole body block — never someone else's leftovers from
      // the static markup
      var tags = (
        item.getAttribute('data-modal-tags') ||
        item.getAttribute('data-modal-person-tags') ||
        ''
      )
        .split(',')
        .map(function (tag) {
          return tag.trim()
        })
        .filter(Boolean)
      var description =
        item.getAttribute('data-modal-description') ||
        item.getAttribute('data-modal-person-description') ||
        ''

      if (personTags) {
        personTags.innerHTML = ''
        tags.forEach(function (text) {
          var tag = document.createElement('div')
          tag.className = 'person-body__tag'
          tag.textContent = text
          personTags.appendChild(tag)
        })
        personTags.style.display = tags.length ? '' : 'none'
      }
      if (personDescription) {
        personDescription.textContent = description
        var descriptionWrap = personDescription.closest(
          '.person-body__description'
        )
        if (descriptionWrap)
          descriptionWrap.style.display = description ? '' : 'none'
      }
      if (personBody)
        personBody.style.display = tags.length || description ? '' : 'none'
    }

    // Section 4 owns every way the modal closes (button, backdrop, Escape),
    // so the unpin watches the is-active class instead of duplicating its
    // close triggers
    new MutationObserver(function () {
      if (!active || modal.classList.contains('is-active')) return
      var item = active
      active = null
      unzoom(item)
      // Resume hover for the card the pointer is already resting on
      if (canHover())
        for (var i = 0; i < items.length; i++) {
          if (items[i].matches(':hover')) {
            zoom(items[i])
            break
          }
        }
    }).observe(modal, { attributeFilter: ['class'] })

    // Item-level handler fires while the click still bubbles inside the
    // card — strictly before section 4's document-level opener, so the
    // modal is always filled by the time it opens
    items.forEach(function (item) {
      item.addEventListener('click', function () {
        fillPersonModal(item)
        active = item
        // Hover-capable pointers pin the zoomed look under the modal;
        // touch skips the effect entirely
        if (canHover()) zoom(item)
      })
    })
  })
})()

/* --------------------------------------------------------------------------
   21. Ventures text: show more / show less. The copy starts fully hidden
   (data-more-lines can leave some lines visible) and the button expands it
   to full height and back. The clamp is applied by JS, not CSS: with
   scripts broken the whole text stays readable rather than silently cut. Height is tweened in px (auto is not animatable)
   and released to auto once open, so the block keeps reflowing with the page.
   -------------------------------------------------------------------------- */

;(function () {
  var LINES = 0 // visible lines while collapsed, [data-more-lines] overrides
  var DURATION = 0.6
  var EASE = 'power3.inOut'
  var LESS_TEXT = 'Show less' // [data-more-less-text] overrides

  onReady(function () {
    toArray('.ventures-text-more').forEach(function (block) {
      var text = block.querySelector('.ventures-text-more__text')
      var button = block.querySelector('.btn-more')
      if (!text || !button) return

      var label =
        button.querySelector('.btn-more__text .caption-m') ||
        button.querySelector('.btn-more__text')
      var moreText = label ? label.textContent.trim() : ''
      var lessText = block.getAttribute('data-more-less-text') || LESS_TEXT
      var lines = parseInt(block.getAttribute('data-more-lines'), 10) || LINES

      // Which of the two icon lines is the vertical one is a Webflow
      // detail, so it is measured rather than assumed — the bounding box
      // accounts for the rotation. CSS then folds that one away, turning
      // the plus into a minus
      toArray('.btn-more__line', button).forEach(function (line) {
        var rect = line.getBoundingClientRect()
        if (rect.height > rect.width) line.classList.add('is-vertical')
      })

      var expanded = false
      var collapsedHeight = 0

      function measure() {
        var inner = text.firstElementChild || text
        var cs = getComputedStyle(inner)
        var lh = parseFloat(cs.lineHeight)
        if (!lh) lh = parseFloat(cs.fontSize) * 1.5 // line-height: normal
        collapsedHeight = Math.round(lh * lines)
      }

      // Content height: overflow is hidden, so scrollHeight stays the full
      // height even while the element is clamped
      function fullHeight() {
        return text.scrollHeight
      }

      function layout() {
        measure()

        // Copy that already fits needs no button at all
        var needed = fullHeight() > collapsedHeight + 2
        button.style.display = needed ? '' : 'none'

        if (!needed) {
          expanded = false
          block.classList.remove('is-active')
          text.style.height = ''
          if (label) label.textContent = moreText
          return
        }

        text.style.height = expanded ? 'auto' : collapsedHeight + 'px'
      }

      function toggle() {
        expanded = !expanded
        block.classList.toggle('is-active', expanded)
        if (label) label.textContent = expanded ? lessText : moreText

        var from = text.getBoundingClientRect().height
        var to = expanded ? fullHeight() : collapsedHeight

        if (!window.gsap) {
          text.style.height = expanded ? 'auto' : collapsedHeight + 'px'
          return
        }

        gsap.killTweensOf(text)
        gsap.fromTo(
          text,
          { height: from },
          {
            height: to,
            duration: DURATION,
            ease: EASE,
            onComplete: function () {
              // Open: hand the height back to the flow, so later reflows
              // (font swap, resize) are not locked to a stale pixel value
              if (expanded) text.style.height = 'auto'
              // The page just got taller or shorter — pinned sections and
              // scroll-driven triggers below need their positions back
              if (window.ScrollTrigger) ScrollTrigger.refresh()
            },
          }
        )
      }

      button.addEventListener('click', function (e) {
        e.preventDefault()
        toggle()
      })

      layout()
      // Line count settles only once webfonts are in
      window.addEventListener('load', layout)
      if (document.fonts) document.fonts.ready.then(layout)

      window.addEventListener('resize', debounce(layout, 150))
    })
  })
})()

/* --------------------------------------------------------------------------
   22. Music intro: the founder photo drifts against the scroll while the
   block crosses the viewport. The logos marquee behind it is left alone —
   it owns its own transform (section 6).

   The photo is a free-standing portrait, not an image inside a frame, so
   the whole element is what moves — a plain vertical translate, nothing
   else. It must NOT be scaled or resized: the photo carries the exact box
   from the design (244×305) and any zoom, combined with a clipping parent,
   eats the top and bottom and flattens that portrait into a square.

   Position is read live from the block on each scroll frame instead of
   going through ScrollTrigger: it needs core GSAP only, and it cannot be
   thrown off by the pin-spacer of .artists shifting the page (the very
   problem section 15 exists to patch for cached triggers).
   -------------------------------------------------------------------------- */

;(function () {
  var SHIFT = 10 // % of the photo's own height travelled in each direction

  onReady(function () {
    if (!window.gsap) return

    var tracked = []

    toArray('.music-intro-main').forEach(function (block) {
      // The photo may be re-classed in Webflow, so fall back to the first
      // direct image — the marquee logos sit deeper, inside the strip
      var photo =
        block.querySelector('.music-intro-main__photo') ||
        block.querySelector(':scope > img')
      if (photo) tracked.push({ block: block, photo: photo })
    })

    if (!tracked.length) return

    var queued = false

    function update() {
      queued = false
      var vh = window.innerHeight

      tracked.forEach(function (entry) {
        var rect = entry.block.getBoundingClientRect()
        // 0 while the block waits just below the fold, 1 once it has fully
        // passed above it — clamped, so off-range scrolling parks the photo
        var progress = (vh - rect.top) / (vh + rect.height)
        progress = Math.min(1, Math.max(0, progress))
        // Drifts downwards as the page scrolls up, so the photo trails the
        // block instead of travelling with it
        gsap.set(entry.photo, { yPercent: (progress * 2 - 1) * SHIFT })
      })
    }

    function onScroll() {
      if (queued) return
      queued = true
      requestAnimationFrame(update)
    }

    update()
    window.addEventListener('scroll', onScroll, { passive: true })
    window.addEventListener('resize', onScroll)
    window.addEventListener('load', update) // lazy photo settles late
  })
})()

/* --------------------------------------------------------------------------
   23. Section nav: the burger menu is the single source of truth — each
   .menu__item carries data-menu-link naming the id of the section it
   points at. The fixed sidebar has no attributes of its own: its rows
   stand for menu entries by position, the footer links match menu entries
   by label. The status line grows towards the active numeral in
   proportion to scroll, the numeral and the matching menu entry carry
   .is-active, and clicking any of the three scrolls smoothly to the
   section. On mobile the sidebar becomes a bottom bar (flex-direction:
   row) — the same progress then drives the status width instead of its
   height. Pages without the sections (legal etc.) run the remote mode:
   every menu entry is plain white and numberless, and leads to the home
   page with #section-<id> in the hash — which the home page, right here,
   turns into a scroll once its layout has settled.
   -------------------------------------------------------------------------- */

;(function () {
  var FOCUS = 0.5 // viewport fraction that decides the active section
  var HOME = '/' // the page that owns the sections the menu links to

  onReady(function () {
    var menu = document.querySelector('.menu')
    var menuItems = toArray('.menu__item')

    // data-menu-link is the id of the section a menu entry points at.
    // Entries without the attribute stay inert
    var ids = menuItems.map(function (item) {
      return item.getAttribute('data-menu-link') || ''
    })

    // Footer links carry the section id themselves in data-anchor-link;
    // a link without the attribute falls back to matching a menu entry by
    // label, so both lists stay in sync while the markup migrates
    function labelOf(el, selector) {
      var node = el.querySelector(selector) || el
      return node.textContent.trim().toLowerCase().replace(/\s+/g, ' ')
    }

    var menuLabels = menuItems.map(function (item) {
      return labelOf(item, '.h4-italic')
    })

    // The "Cities" column shares the link class but belongs to the offices
    // accordion (section 28) — those links must not double as section nav
    var footerLinks = toArray('.footer-top-nav__link').filter(function (link) {
      return !link.hasAttribute('data-office-scroll-to')
    })
    // The footer nav never shows an active state — drop any is-active the
    // Webflow markup shipped with
    footerLinks.forEach(function (link) {
      link.classList.remove('is-active')
    })
    var footerIds = footerLinks.map(function (link, i) {
      var own = link.getAttribute('data-anchor-link')
      if (own) return own
      var match = menuLabels.indexOf(labelOf(link, '.caption-m'))
      return match !== -1 ? ids[match] : ids[i] || ''
    })

    // Loose anchors: any other [data-anchor-link] element on the page
    // scrolls to its section the same way
    var extraAnchors = toArray('[data-anchor-link]').filter(function (el) {
      return footerLinks.indexOf(el) === -1 && menuItems.indexOf(el) === -1
    })
    var extraIds = extraAnchors.map(function (el) {
      return el.getAttribute('data-anchor-link') || ''
    })

    // Menu entries resolved to their sections; ids missing from the page
    // drop out, each entry remembers its menu position
    var entries = []
    ids.forEach(function (id, i) {
      var el = id ? document.getElementById(id) : null
      if (el) entries.push({ id: id, el: el, index: i })
    })

    // Remote mode: no sections on this page — the menu and the footer
    // links only point home, the hash carries the target id
    if (!entries.length) {
      if (menu) menu.classList.add('is-remote')

      function bindRemote(items, itemIds) {
        items.forEach(function (item, i) {
          item.addEventListener('click', function (e) {
            if (!itemIds[i]) return
            e.preventDefault()
            window.location.href = HOME + '#section-' + itemIds[i]
          })
        })
      }

      bindRemote(menuItems, ids)
      bindRemote(footerLinks, footerIds)
      bindRemote(extraAnchors, extraIds)
      return
    }

    // The progress scan walks section tops downward, so entries follow
    // document order even if the menu lists them differently
    entries.sort(function (a, b) {
      return a.el.compareDocumentPosition(b.el) &
        Node.DOCUMENT_POSITION_FOLLOWING
        ? -1
        : 1
    })

    var container = document.querySelector('.nav-sidebar__items')
    var status = container
      ? container.querySelector('.nav-sidebar__status')
      : null
    var sideItems = container ? toArray('.nav-sidebar-item', container) : []

    // Sidebar rows have no attributes of their own: row i stands for menu
    // entry i. This picks the numeral answering for each section — the
    // status line ruler
    var itemForSection = entries.map(function (entry) {
      return sideItems[entry.index] || null
    })

    /* ---- anchors -------------------------------------------------------- */

    // The fixed header covers the top strip of the viewport — landing
    // exactly on the section top would hide its head under it. Measured
    // live: the height differs per breakpoint
    function targetTop(section) {
      var header = document.querySelector('.header')
      var headerHeight = header ? header.offsetHeight : 0
      return Math.max(
        0,
        section.getBoundingClientRect().top + window.pageYOffset - headerHeight
      )
    }

    // Lazy images above the target keep landing while the smooth scroll is
    // in flight, growing the page and dragging the section away from where
    // the scroll was aimed. The settle loop below re-aims until the target
    // holds still; a wheel or touch from the user calls the whole thing off
    var settleTimer = null
    function stopSettle() {
      clearInterval(settleTimer)
      settleTimer = null
      window.removeEventListener('wheel', stopSettle)
      window.removeEventListener('touchstart', stopSettle)
    }

    function scrollToSection(section) {
      var commanded = targetTop(section)
      function aim() {
        smoothScrollTo(commanded)
      }
      aim()

      stopSettle()
      window.addEventListener('wheel', stopSettle, { passive: true })
      window.addEventListener('touchstart', stopSettle, { passive: true })

      var started = Date.now()
      settleTimer = setInterval(function () {
        var expected = targetTop(section)
        var arrived = Math.abs(window.pageYOffset - expected) < 2
        if (arrived || Date.now() - started > 3000) {
          stopSettle()
          return
        }
        if (Math.abs(expected - commanded) > 1) {
          commanded = expected
          aim()
        }
      }, 250)
    }

    function entryFor(id) {
      for (var k = 0; k < entries.length; k++) {
        if (entries[k].id === id) return entries[k]
      }
      return null
    }

    function bind(items, itemIds) {
      items.forEach(function (item, i) {
        item.addEventListener('click', function (e) {
          var entry = entryFor(itemIds[i])
          if (!entry) return
          e.preventDefault()

          // A click inside the burger menu first closes it through its own
          // toggle (section 3), which also unlocks page scroll
          var burger = document.querySelector('.btn-burger.is-active')
          if (burger) burger.click()

          scrollToSection(entry.el)
        })
      })
    }

    bind(sideItems, ids)
    bind(menuItems, ids)
    bind(footerLinks, footerIds)
    bind(extraAnchors, extraIds)

    // Arrived from a remote page: #section-<id> names a section by id.
    // The scroll waits for window load, which lands after section 15's
    // ScrollTrigger.refresh — so the .artists pin-spacer is already in the
    // layout and the target position is final
    var arrival = /^#section-(.+)$/.exec(window.location.hash)
    if (arrival) {
      var target = entryFor(arrival[1])
      if (target) {
        window.addEventListener('load', function () {
          scrollToSection(target.el)
        })
      }
    }

    /* ---- progress + active state ---------------------------------------- */

    var currentIndex = -1
    var queued = false

    function setActive(menuIndex) {
      if (menuIndex === currentIndex) return
      currentIndex = menuIndex

      sideItems.forEach(function (item, i) {
        item.classList.toggle('is-active', i === menuIndex)
      })
      menuItems.forEach(function (item, i) {
        var active = i === menuIndex
        item.classList.toggle('is-active', active)

        // Only the active entry shows its numeral: it rides in from the
        // left (the same move the menu reveal used to make) while the
        // previous one folds back out
        var numeral = item.querySelector('.menu__number')
        if (numeral && window.gsap)
          gsap.to(numeral, {
            xPercent: active ? 0 : -60,
            autoAlpha: active ? 1 : 0,
            duration: active ? 0.5 : 0.35,
            ease: active ? 'power3.out' : 'power2.out',
            overwrite: 'auto',
          })
      })
    }

    function update() {
      queued = false

      // Fractional position across section starts: 1.5 = halfway between
      // the tops of sections 2 and 3. Anchors are read live every frame —
      // the .artists pin-spacer and lazy images keep shifting them long
      // after init, so cached values would drift (see section 15)
      var focus = window.pageYOffset + window.innerHeight * FOCUS
      var tops = entries.map(function (entry) {
        return entry.el.getBoundingClientRect().top + window.pageYOffset
      })

      var last = entries.length - 1
      var f = 0
      if (focus >= tops[last]) {
        f = last
      } else {
        for (var i = last - 1; i >= 0; i--) {
          if (focus >= tops[i]) {
            f = i + (focus - tops[i]) / (tops[i + 1] - tops[i])
            break
          }
        }
      }

      setActive(entries[Math.min(last, Math.floor(f))].index)

      if (!status) return

      var i0 = Math.min(last, Math.floor(f))
      var i1 = Math.min(last, i0 + 1)
      var from = itemForSection[i0]
      var to = itemForSection[i1]
      if (!from || !to) return

      var t = f - i0

      // Desktop: vertical rail — the line grows down to the active numeral.
      // Mobile bottom bar: the same progress drives the width. The unused
      // axis is released back to the stylesheet value
      if (getComputedStyle(container).flexDirection.indexOf('row') === 0) {
        var fromRight = from.offsetLeft + from.offsetWidth
        var toRight = to.offsetLeft + to.offsetWidth
        status.style.width = fromRight + (toRight - fromRight) * t + 'px'
        status.style.height = ''
      } else {
        var fromBottom = from.offsetTop + from.offsetHeight
        var toBottom = to.offsetTop + to.offsetHeight
        status.style.height = fromBottom + (toBottom - fromBottom) * t + 'px'
        status.style.width = ''
      }
    }

    function onScroll() {
      if (queued) return
      queued = true
      requestAnimationFrame(update)
    }

    update()
    window.addEventListener('scroll', onScroll, { passive: true })
    window.addEventListener('resize', onScroll)
    window.addEventListener('load', update)
  })
})()

/* --------------------------------------------------------------------------
   24. Legal pages: the same masked line reveal the modals use (section 4),
   but scroll-driven — each text block splits into lines and they rise out
   of their masks once the block enters the viewport. autoSplit + a fresh
   context per split is the section-7 pattern: lines recalculate themselves
   on font load and width changes without manual resize plumbing.
   -------------------------------------------------------------------------- */

;(function () {
  onReady(function () {
    if (!window.gsap || !window.ScrollTrigger || !window.SplitText) return

    var targets = toArray('.legal-text__in .body-l, .legal-text__in .list__item')
    if (!targets.length) return

    gsap.registerPlugin(ScrollTrigger, SplitText)

    targets.forEach(function (el) {
      revealReady(el)
      // The trigger and the split have independent lifecycles on purpose:
      // autoSplit re-splits on font load/resize and would take a trigger
      // living inside onSplit down with it — if that happened mid-reveal,
      // the text stayed masked forever (visible as ordered lists showing
      // bare CSS counters). The trigger is created once and never dies;
      // onSplit only syncs fresh lines with the "revealed" flag
      var state = { lines: null, revealed: false }

      new SplitText(el, {
        type: 'lines',
        mask: 'lines',
        linesClass: 'line',
        autoSplit: true,
        onSplit: function (self) {
          if (state.lines) gsap.killTweensOf(state.lines)
          state.lines = self.lines
          gsap.set(self.lines, { yPercent: state.revealed ? 0 : 110 })
        },
      })

      ScrollTrigger.create({
        trigger: el,
        start: 'top 88%',
        // no `once`: see section 30 — the flag keeps this one-shot
        onEnter: function () {
          if (state.revealed) return
          state.revealed = true
          if (state.lines)
            gsap.to(state.lines, {
              yPercent: 0,
              duration: 0.8,
              stagger: 0.06,
              ease: 'expo.out',
            })
        },
      })
    })
  })
})()

/* --------------------------------------------------------------------------
   25. Universal track slider. Any block becomes a slider from the markup
   alone — no new JS per block, which matters because on mobile a lot of
   grids turn into sliders. The children are moved into a runtime track
   that steps by exactly one card; Webflow keeps its plain grid, so the
   designer and the no-JS fallback stay intact.

   Attributes on the element whose children are the slides:
     data-slider              on = always; a number = max width it runs at
                              (data-slider="767" → mobile/tablet only)
     data-slider-item         selector for slides (default: all children)
     data-slider-visible      cards per view (default 1)
     data-slider-visible-991  per-breakpoint overrides, Webflow's widths;
     data-slider-visible-767  the smallest matching one wins
     data-slider-visible-479
     data-slider-prev/next    button selectors (default .btn-prev/.btn-next
                              looked up in the closest section)
     data-slider-gap          gutter override (default: the element's own
                              computed column-gap, so Webflow owns it)
   -------------------------------------------------------------------------- */

;(function () {
  var DURATION = 0.65
  var EASE = 'power3.inOut'
  var BREAKPOINTS = [991, 767, 479] // descending: the last match wins

  // Known blocks are decorated here rather than in Webflow, so their
  // markup stays clean — same approach section 13 takes for m-and-a-sell
  function decorate() {
    toArray('.m-and-a-insights__items').forEach(function (el) {
      if (el.hasAttribute('data-slider')) return
      el.setAttribute('data-slider', '')
      el.setAttribute('data-slider-item', '.m-and-a-insights-item')
      el.setAttribute('data-slider-visible', '4')
      el.setAttribute('data-slider-visible-991', '3')
      el.setAttribute('data-slider-visible-767', '2')
      el.setAttribute('data-slider-visible-479', '1')
      el.setAttribute('data-slider-peek-767', '1.5rem')
    })

    toArray('.big-move__items').forEach(function (el) {
      if (el.hasAttribute('data-slider')) return
      el.setAttribute('data-slider', '')
      el.setAttribute('data-slider-item', '.big-move-item')
      el.setAttribute('data-slider-visible', '4')
      el.setAttribute('data-slider-visible-991', '3')
      el.setAttribute('data-slider-visible-767', '2')
      el.setAttribute('data-slider-visible-479', '1')
      el.setAttribute('data-slider-peek-767', '1.5rem')
    })

    // Football clients: a plain grid everywhere, one full-width card per
    // swipe below the mobile breakpoint. The attributes lived on the
    // static Webflow element and were lost when the block became a
    // collection
    toArray('.football-clients__items').forEach(function (el) {
      if (el.hasAttribute('data-slider')) return
      el.setAttribute('data-slider', '479')
      el.setAttribute('data-slider-item', '.football-clients-item')
      el.setAttribute('data-slider-visible', '1')
    })
  }

  function matches(width) {
    return window.matchMedia && matchMedia('(max-width: ' + width + 'px)').matches
  }

  function setup(viewport) {
    var itemSelector = viewport.getAttribute('data-slider-item')
    var items = itemSelector
      ? toArray(itemSelector, viewport)
      : toArray(':scope > *', viewport)

    // Silence here would look like "the slider is broken": the usual cause
    // is data-slider-item holding a class name without its dot, which is a
    // valid selector that simply matches nothing
    if (items.length < 2) {
      if (window.console)
        console.warn(
          '[slider] needs at least 2 slides, found ' +
            items.length +
            (itemSelector
              ? ' for data-slider-item="' + itemSelector + '"'
              : ' among the children'),
          viewport
        )
      return
    }

    var limitAttr = parseInt(viewport.getAttribute('data-slider'), 10)

    // Arrows: an explicit selector wins, otherwise the nearest .btn-prev /
    // .btn-next above the slider — a plain <div> block works as well as a
    // <section>. The walk stops before <body>, so a block without arrows
    // never borrows another section's
    function findButton(attr, fallback) {
      var custom = viewport.getAttribute(attr)
      if (custom) return document.querySelector(custom)

      var node = viewport.parentNode
      while (node && node !== document.body && node.querySelector) {
        var found = node.querySelector(fallback)
        if (found) return found
        node = node.parentNode
      }
      return null
    }

    var prev = findButton('data-slider-prev', '.btn-prev')
    var next = findButton('data-slider-next', '.btn-next')

    var track = null
    var enabled = false
    var index = 0
    var step = 0
    var visible = 1
    var maxIndex = 0
    var maxOffset = 0
    var observer = null
    var swiped = false
    var swipeTimer = null

    // Capture phase: the guard has to run before the link's own default,
    // and before any handler bound to a card
    viewport.addEventListener(
      'click',
      function (e) {
        if (!swiped) return
        e.preventDefault()
        e.stopPropagation()
      },
      true
    )

    // Any option can be given per breakpoint with a -991/-767/-479 suffix;
    // the smallest matching one wins
    function option(name) {
      var value = viewport.getAttribute('data-slider-' + name)
      BREAKPOINTS.forEach(function (bp) {
        var override = viewport.getAttribute('data-slider-' + name + '-' + bp)
        if (override !== null && matches(bp)) value = override
      })
      return value
    }

    function visibleCount() {
      return parseInt(option('visible'), 10) || 1
    }

    // Lengths accept rem/em/%/px (bare numbers are px), so a value can be
    // written in whatever unit the design is authored in. rem is tested
    // first — "rem" also ends with "em"
    function toPx(raw) {
      if (!raw) return 0
      var value = parseFloat(raw) || 0
      if (/rem\s*$/.test(raw))
        return (
          value *
          parseFloat(getComputedStyle(document.documentElement).fontSize)
        )
      if (/em\s*$/.test(raw))
        return value * parseFloat(getComputedStyle(viewport).fontSize)
      if (/%\s*$/.test(raw)) return (viewport.clientWidth * value) / 100
      return value
    }

    // Strip of the next card left showing at the edge
    function peekWidth() {
      return toPx(option('peek'))
    }

    function shouldRun() {
      return !limitAttr || matches(limitAttr)
    }

    // Widths are dealt in px for exactly `visible` per view: a percentage
    // basis ignores the gutter and leaves fractional cards at the edge
    function measure() {
      var gapAttr = option('gap')
      var gap = gapAttr
        ? toPx(gapAttr)
        : parseFloat(getComputedStyle(viewport).columnGap) || 0

      visible = visibleCount()
      track.style.columnGap = gap ? gap + 'px' : ''

      // The peeking card needs its own gutter on top of the ones between
      // the fully visible cards
      var peek = peekWidth()
      var gaps = visible - 1 + (peek > 0 ? 1 : 0)
      var width = (viewport.clientWidth - peek - gaps * gap) / visible

      items.forEach(function (item) {
        item.style.flex = '0 0 auto'
        item.style.width = width + 'px'
      })

      step = width + gap

      // Travel is measured against the real content width, so the last
      // card lands flush with the right edge instead of stopping a peek
      // short of it
      var total = items.length * width + (items.length - 1) * gap
      maxOffset = Math.max(0, total - viewport.clientWidth)
      maxIndex = step ? Math.ceil(maxOffset / step) : 0
    }

    function updateButtons() {
      if (prev) prev.classList.toggle('is-disabled', index === 0)
      if (next) next.classList.toggle('is-disabled', index >= maxIndex)
    }

    function goTo(target, animate) {
      if (!enabled) return
      index = Math.max(0, Math.min(maxIndex, target))
      updateButtons()
      gsap.to(track, {
        x: -Math.min(index * step, maxOffset),
        duration: animate === false ? 0 : DURATION,
        ease: EASE,
        overwrite: 'auto',
      })
    }

    function enable() {
      if (enabled) return
      enabled = true

      track = document.createElement('div')
      track.className = 'slider__track'
      viewport.insertBefore(track, items[0])
      items.forEach(function (item) {
        track.appendChild(item)
      })
      viewport.classList.add('is-slider')

      measure()
      index = Math.min(index, maxIndex)
      goTo(index, false)

      // One card per gesture — touch and mouse drag alike. The flag opens
      // only while a real drag is in progress (the section-13 pattern), so
      // a plain pointer move never slides; the cards are often links, and
      // the click a browser fires after the gesture is swallowed by the
      // capture-phase guard above
      if (window.Observer) {
        var used = true
        observer = Observer.create({
          target: viewport,
          type: 'touch,pointer',
          dragMinimum: 10,
          tolerance: 25,
          lockAxis: true,
          onDragStart: function () {
            used = false
          },
          // A swipe must not count as a tap: the slider itself can be a
          // link (the music block wraps its cards in one), and browsers
          // still fire a click after the gesture
          onDragEnd: function () {
            used = true
            swiped = true
            clearTimeout(swipeTimer)
            swipeTimer = setTimeout(function () {
              swiped = false
            }, 300)
          },
          onLeft: function (self) {
            if (!used && self.isDragging) {
              used = true
              goTo(index + 1)
            }
          },
          onRight: function (self) {
            if (!used && self.isDragging) {
              used = true
              goTo(index - 1)
            }
          },
        })
      }
    }

    // Leaving slider mode puts the markup back exactly as Webflow built
    // it: every inline style this code wrote is dropped, so the original
    // grid takes over again
    function disable() {
      if (!enabled) return
      enabled = false

      if (observer) {
        observer.kill()
        observer = null
      }
      gsap.killTweensOf(track)

      items.forEach(function (item) {
        viewport.insertBefore(item, track)
        item.style.flex = ''
        item.style.width = ''
      })
      if (track.parentNode) track.parentNode.removeChild(track)
      track = null

      viewport.classList.remove('is-slider')
      if (prev) prev.classList.remove('is-disabled')
      if (next) next.classList.remove('is-disabled')
    }

    function sync() {
      if (shouldRun()) {
        if (enabled) {
          measure()
          goTo(index, false)
        } else {
          enable()
        }
      } else {
        disable()
      }
    }

    if (prev)
      prev.addEventListener('click', function (e) {
        e.preventDefault()
        goTo(index - 1)
      })
    if (next)
      next.addEventListener('click', function (e) {
        e.preventDefault()
        goTo(index + 1)
      })

    sync()
    window.addEventListener('resize', debounce(sync, 150))
  }

  onReady(function () {
    if (!window.gsap) return
    decorate()
    toArray('[data-slider]').forEach(setup)
  })
})()

/* --------------------------------------------------------------------------
   26. Show more: [data-show-more="N"] on a button keeps the first N items
   of its collection visible and folds the rest away; the click reveals
   them and retires the button. Items default to Webflow CMS items of the
   nearest collection above the button ([data-show-more-items] overrides
   the selector). Folding is for PHONES: it applies up to MAX_WIDTH, wider
   screens always show everything, and crossing the breakpoint re-syncs on
   the fly. [data-show-more-max] moves that limit per block — "0" drops it
   and folds at every width.
   -------------------------------------------------------------------------- */

;(function () {
  var ITEM = '.w-dyn-item' // Webflow CMS collection item
  var MAX_WIDTH = 479 // the width Webflow stacks its grids at

  onReady(function () {
    // Known non-CMS blocks are decorated here, keeping the markup clean
    toArray('.hits__button [data-show-more]').forEach(function (btn) {
      if (!btn.hasAttribute('data-show-more-items'))
        btn.setAttribute('data-show-more-items', '.hits-item')
    })

    toArray('[data-show-more]').forEach(function (button) {
      var limit = parseInt(button.getAttribute('data-show-more'), 10)
      if (!limit) return

      var selector = button.getAttribute('data-show-more-items') || ITEM

      // Only an explicit number moves the breakpoint: a missing attribute
      // read as NaN, and !NaN kept the fold active at every width — which is
      // how the team grid ended up hiding two thirds of itself on desktop
      var maxAttr = parseInt(button.getAttribute('data-show-more-max'), 10)
      var maxWidth = isNaN(maxAttr) ? MAX_WIDTH : maxAttr

      function active() {
        return (
          !maxWidth ||
          (window.matchMedia &&
            matchMedia('(max-width: ' + maxWidth + 'px)').matches)
        )
      }

      // Nearest ancestor that actually holds items — other collections on
      // the page stay out of it
      var items = []
      var scope = button.parentNode
      while (scope && scope !== document && !items.length) {
        items = toArray(selector, scope)
        scope = scope.parentNode
      }

      // A button alone in its wrapper hides the wrapper instead: an empty
      // flex/grid child would still take its gap in the layout
      var host = button.parentNode
      var toggle =
        host && host.children.length === 1 && host !== document.body
          ? host
          : button

      if (items.length <= limit) {
        toggle.classList.add('is-hidden')
        return
      }

      var hidden = items.slice(limit)
      var expanded = false

      // One owner for the class: the button belongs to the fold, so it goes
      // away wherever the fold does — above the breakpoint, or once the list
      // has been expanded and there is nothing left to reveal
      function sync() {
        var fold = active() && !expanded
        hidden.forEach(function (item) {
          item.classList.toggle('is-hidden', fold)
        })
        toggle.classList.toggle('is-hidden', !fold)
      }

      button.addEventListener('click', function (e) {
        e.preventDefault()
        expanded = true
        sync()

        if (window.gsap)
          gsap.from(hidden, {
            y: 24,
            duration: 0.6,
            ease: 'power3.out',
            stagger: 0.02,
          })

        // The page just grew — pinned and scroll-driven triggers below
        // need their positions recalculated
        if (window.gsap && window.ScrollTrigger) ScrollTrigger.refresh()
      })

      sync()
      window.addEventListener('resize', debounce(sync, 150))
    })
  })
})()

/* --------------------------------------------------------------------------
   27. Stat items rise and fade in as their row reaches the viewport, just
   ahead of the digits rolling (section 14 gives every .stat-intro-stat__row
   its own odometer group). Empty items are layout spacers and are skipped.
   -------------------------------------------------------------------------- */

;(function () {
  onReady(function () {
    if (!window.gsap || !window.ScrollTrigger) return

    toArray('.stat-intro-stat__item').forEach(revealReady)

    toArray('.stat-intro-stat__row').forEach(function (row) {
      var items = toArray('.stat-intro-stat__item', row).filter(function (
        item
      ) {
        return item.children.length
      })
      if (!items.length) return

      // Starts slightly earlier than the odometer's own trigger, so a stat
      // is already on screen by the time its number starts counting
      gsap.from(items, {
        autoAlpha: 0,
        y: 24,
        duration: 0.8,
        stagger: 0.12,
        ease: 'power3.out',
        scrollTrigger: {
          trigger: row,
          start: 'top 88%',
          // no `once`: see section 30 — a from() plays once anyway
        },
      })
    })
  })
})()

/* --------------------------------------------------------------------------
   28. Footer cities: [data-office-scroll-to="Name"] scrolls to the offices
   accordion and opens the [data-office-target] item with that name — the
   opener is exposed by section 10, so both travel and the fold-out run at
   once. Pages without the block send the visitor home with #office-Name in
   the hash, and the home page resolves it on load — the same pattern the
   section nav uses for #section-N.
   -------------------------------------------------------------------------- */

;(function () {
  var HOME = '/' // the page that owns the offices block

  onReady(function () {
    var links = toArray('[data-office-scroll-to]')
    var container = document.querySelector('.milk-honey-slider__items')
    if (!links.length && !container) return

    function openOffice(name) {
      if (container && container._openOffice) container._openOffice(name)
    }

    function scrollToBlock() {
      var block = container.closest('.milk-honey-slider') || container
      var top = block.getBoundingClientRect().top + window.pageYOffset
      smoothScrollTo(top)
    }

    links.forEach(function (link) {
      link.addEventListener('click', function (e) {
        e.preventDefault()
        var name = link.getAttribute('data-office-scroll-to')

        if (!container) {
          window.location.href = HOME + '#office-' + encodeURIComponent(name)
          return
        }

        openOffice(name) // the accordion folds out while the page travels
        scrollToBlock()
      })
    })

    // Arrived from a remote page: resolve the hash once the layout has
    // settled (after section 15's ScrollTrigger.refresh on load)
    var arrival = container && /^#office-(.+)$/.exec(window.location.hash)
    if (arrival) {
      var name = decodeURIComponent(arrival[1])
      window.addEventListener('load', function () {
        openOffice(name)
        scrollToBlock()
      })
    }
  })
})()

/* --------------------------------------------------------------------------
   29. Header reveal: [data-animation-name="header"]. Webflow's "Classic
   Text Reveal II": every line sits in its own overflow mask and rises out
   of it while fading in once the heading is 30% of the viewport up from
   the bottom (start: 'top 70%'). Plays once per page view: a `played`
   flag guards show() and the trigger has no leave handlers, so scrolling
   away and back never replays it. Not `once: true` — see section 30 for
   the refresh-loop bug that rules it out.
   Split + trigger keep separate lifecycles (section 24):
   autoSplit re-splits on font load and resize, onSplit only syncs the
   fresh lines with the "shown" flag. Headings that also carry
   data-reveal-text-to are left to section 7 — two autoSplits on one
   element fight over the markup.
   -------------------------------------------------------------------------- */

;(function () {
  var Y_HIDDEN = 110 // percent of line height: fully below the mask
  var SHOW = { duration: 1, stagger: 0.05, ease: 'quint.out' }
  var START = 'top 70%'
  var END = 'bottom top'
  var BASE_LINE = 70 // px of line height the base duration is tuned for
  var MAX_SCALE = 1.8 // giant headings (.h1-italic ~284px) take up to this ×

  // One duration for every heading made the giant ones travel their ~284px
  // in the same second as a 70px line — several times the pixel speed, and
  // it read as a jolt. Time scales with the square root of the line height,
  // so the perceived pace stays the same from .h6 up to .h1-italic
  function paceFor(lines) {
    var h = lines[0] ? lines[0].offsetHeight : BASE_LINE
    return gsap.utils.clamp(1, MAX_SCALE, Math.sqrt(h / BASE_LINE))
  }

  onReady(function () {
    if (!window.gsap || !window.ScrollTrigger || !window.SplitText) return

    var targets = toArray('[data-animation-name="header"]').filter(function (
      el
    ) {
      return !el.hasAttribute('data-reveal-text-to')
    })
    if (!targets.length) return

    gsap.registerPlugin(ScrollTrigger, SplitText)

    targets.forEach(function (el) {
      var state = { lines: null, shown: false, split: null, played: false }

      function show() {
        if (state.played) return
        state.played = true
        state.shown = true
        if (!state.lines) return
        var pace = paceFor(state.lines)
        gsap.to(state.lines, {
          yPercent: 0,
          autoAlpha: 1,
          duration: SHOW.duration * pace,
          stagger: SHOW.stagger,
          ease: SHOW.ease,
          overwrite: true,
        })
      }

      function splitNow() {
        revealReady(el)
        // The heading marquee (6b) owns this element right now — a split
        // would slice its looping track apart
        if (el._tween) return
        if (state.split) {
          try {
            state.split.revert()
          } catch (e) {
            /* the old split's nodes are already gone */
          }
          state.split = null
          state.lines = null
        }
        state.split = new SplitText(el, {
          type: 'lines',
          mask: 'lines',
          linesClass: 'line',
          autoSplit: true,
          onSplit: function (self) {
            if (state.lines) gsap.killTweensOf(state.lines)
            state.lines = self.lines

            // Italic caps overhang the line box sideways; give the mask some
            // horizontal headroom so the first and last glyph aren't clipped.
            // Inline margins don't collapse, so the layout is unchanged
            gsap.set(self.masks, {
              paddingInline: '0.2em',
              marginInline: '-0.2em',
            })

            gsap.set(
              self.lines,
              state.shown
                ? { yPercent: 0, autoAlpha: 1 }
                : { yPercent: Y_HIDDEN, autoAlpha: 0 }
            )
          },
        })
      }

      // 6b calls this after it restores a heading's original markup
      el._revealRebuild = splitNow
      splitNow()

      ScrollTrigger.create({
        trigger: el,
        start: START,
        end: END,
        onEnter: show,
        onEnterBack: show,
      })
    })
  })
})()

/* --------------------------------------------------------------------------
   30. Card reveal: collection cards rise and fade in as they enter the
   viewport, cascading left to right (DOM order) 0.1s apart — the
   amessagefrom.earth timing: y over 0.9s cubic-out, opacity over 0.7s
   starting 0.1s later. ScrollTrigger.batch animates only the cards that
   actually enter, so a tall grid cascades row by row instead of trailing
   one long stagger. Groups are listed here, not in Webflow. Cards folded
   by show-more (section 26) are skipped: they appear on the click as
   before. Bordered cards (hits, deals, music, insights) animate their
   inner parts instead of the card itself, so the grid lines never move —
   a card's parts share one delay, the cascade stays per card. Finished
   elements get their inline transform/opacity cleared, so a leftover
   transform can't open a stacking context under the football hover
   (section 20 lifts photos above neighbours by z-index). A fast
   travel (anchor links, hash arrival) sweeps many cards into one batch:
   cards already scrolled past just show, and the cascade of the rest is
   capped so nothing trails for seconds. Any CSS transition on a card is
   paused for the reveal so it can't smear the tween. Phones get a much
   smaller lift. Reduced motion: cards stay as they are.
   No `once: true` here (nor in 24/27): a once-trigger that is already
   past its end when ScrollTrigger refreshes kills itself INSIDE the
   refresh loop, the trigger array shrinks under the loop and refresh
   aborts with "reading 'end'" — leaving every trigger on the page
   without positions (a reload landing mid-page did exactly that).
   Triggers are killed from the batch callback instead, which runs later
   via delayedCall, outside any refresh.
   -------------------------------------------------------------------------- */

;(function () {
  // card: what triggers and paces the cascade. inner: what moves — the
  // card's own parts when it has a border, the card itself otherwise.
  // Descendant selectors on purpose: section 25 moves several of these
  // lists into a runtime .slider__track, and section 16 rebuilds hits rows
  var GROUPS = [
    {
      card: '.big-move__items .big-move-item',
      inner: '.big-move-item__head, .big-move-item__footer',
    },
    { card: '.hits__row .hits-item', inner: '.hits-item__visual, .hits-item__content' },
    { card: '.ventures__body .ventures-text, .ventures__body .ventures__visual' },
    { card: '.football-clients__items .football-clients-item' },
    { card: '.music__items .music-item', inner: '.music-item__visual, .music-item__text' },
    {
      card: '.m-and-a-insights__items .m-and-a-insights-item',
      inner: '.m-and-a-insights-item__content, .m-and-a-insights-item__footer',
    },
  ]
  var Y = 80
  var Y_PHONE = 24
  var PHONE = '(max-width: 767px)'
  var START = 'top 85%'
  var STAGGER = 0.1
  var MAX_TRAIL = 0.8 // longest cascade per batch, whatever its size
  var CLEAR = 'transform,opacity,visibility,transition'

  onReady(function () {
    if (!window.gsap || !window.ScrollTrigger) return
    var reduced =
      window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches

    var cards = []
    var movers = [] // everything that actually animates
    GROUPS.forEach(function (group) {
      toArray(group.card).forEach(function (card) {
        var parts = group.inner ? toArray(group.inner, card) : []
        parts = parts.length ? parts : [card]
        parts.forEach(revealReady) // CSS guard off — JS owns them now
        if (reduced) return // guard lifted, cards stay as they are
        if (card.classList.contains('is-hidden')) return
        if (cards.indexOf(card) !== -1) return
        card._revealParts = parts
        cards.push(card)
        movers = movers.concat(card._revealParts)
      })
    })
    if (!cards.length) return

    function partsOf(list) {
      return list.reduce(function (all, card) {
        return all.concat(card._revealParts)
      }, [])
    }

    var lift =
      window.matchMedia && window.matchMedia(PHONE).matches ? Y_PHONE : Y
    gsap.set(movers, { autoAlpha: 0, y: lift, transition: 'none' })

    ScrollTrigger.batch(cards, {
      start: START,
      onEnter: function (batch, triggers) {
        var passed = []
        var entering = []
        batch.forEach(function (card) {
          if (card._revealed) return
          card._revealed = true
          var rect = card.getBoundingClientRect()
          if (rect.bottom < 0) passed.push(card)
          else entering.push(card)
        })
        triggers.forEach(function (t) {
          t.kill()
        })
        if (passed.length) gsap.set(partsOf(passed), { clearProps: CLEAR })
        if (!entering.length) return

        var each = Math.min(STAGGER, MAX_TRAIL / entering.length)
        var all = partsOf(entering)

        entering.forEach(function (card, i) {
          var parts = card._revealParts
          var delay = i * each
          // The lift outlasts the fade, so the last one owns the cleanup
          gsap.to(parts, {
            y: 0,
            duration: 0.9,
            delay: delay,
            ease: 'power2.out',
            onComplete:
              i === entering.length - 1
                ? function () {
                    gsap.set(all, { clearProps: CLEAR })
                  }
                : null,
          })
          gsap.to(parts, {
            autoAlpha: 1,
            duration: 0.7,
            delay: delay + 0.1,
            ease: 'power1.out',
          })
        })
      },
    })
  })
})()

/* --------------------------------------------------------------------------
   31. Hits: on hover the vinyl (.hits-item__vinyl, sitting behind the cover
   at z-index -1) slides out to the right from behind the album cover and
   the title nudges over a few pixels; both slide back on leave. Desktop
   only: hover-capable fine pointers above the phone
   breakpoint — a touch "hover" would leave the record stuck out.
   -------------------------------------------------------------------------- */

;(function () {
  var MEDIA = '(hover: hover) and (pointer: fine) and (min-width: 768px)'
  var SHIFT = 20 // vinyl: percent of its own width that peeks out
  var TEXT_SHIFT = 10 // title: px to the right
  var OUT = { duration: 0.55, ease: 'power3.out' }
  var BACK = { duration: 0.45, ease: 'power3.inOut' }

  onReady(function () {
    if (!window.gsap || !window.matchMedia) return
    if (!window.matchMedia(MEDIA).matches) return

    toArray('.hits-item').forEach(function (item) {
      var vinyl = item.querySelector('.hits-item__vinyl')
      var content = item.querySelector('.hits-item__content')
      if (!vinyl) return

      gsap.set(vinyl, { xPercent: 0 })

      item.addEventListener('mouseenter', function () {
        gsap.to(vinyl, {
          xPercent: SHIFT,
          duration: OUT.duration,
          ease: OUT.ease,
          overwrite: 'auto',
        })
        if (content)
          gsap.to(content, {
            x: TEXT_SHIFT,
            duration: OUT.duration,
            ease: OUT.ease,
            overwrite: 'auto',
          })
      })
      item.addEventListener('mouseleave', function () {
        gsap.to(vinyl, {
          xPercent: 0,
          duration: BACK.duration,
          ease: BACK.ease,
          overwrite: 'auto',
        })
        if (content)
          gsap.to(content, {
            x: 0,
            duration: BACK.duration,
            ease: BACK.ease,
            overwrite: 'auto',
          })
      })
    })
  })
})()

/* --------------------------------------------------------------------------
   32. Line drawing: any inline <svg data-draw-svg> is drawn as one
   continuous line when it scrolls in. Three kinds of file work:
     - a Figma "inside stroke" export (shape in <mask>, the visible part is
       that stroke outlined and clipped — a fill, nothing to draw): the mask
       shape is stroked in the fill's own colour/opacity and the fill hidden;
     - a plain export with stroked shapes: every path/line/polyline/polygon/
       circle/ellipse/rect is drawn in document order, one after another,
       each taking its share of the time so the pen keeps one speed;
     - an "outline stroke" export with neither (filled paths tracing a thick
       line, no mask, no stroke): the fill is revealed along its own course
       through a generated <mask> — the same path stroked wide — so the
       filled doodle appears as if a pen were laying it down; the mask is
       removed once the draw completes, leaving the svg untouched.
   Attributes (all optional):
     data-draw-svg-duration  seconds, default 3
     data-draw-svg-start     ScrollTrigger start, default "top 75%"
     data-draw-svg-stroke    viewBox units. Mask kind: the drawn line's
                             width (an export's inside stroke is 1 unit),
                             default 1. Fill kind: the reveal stroke's
                             width — make it wider than the doodle's line,
                             default 6% of the viewBox
     data-draw-svg-trigger   selector resolved with closest() for the
                             trigger element, default the svg itself
   Known decorations without the attribute are decorated here. Draws once;
   no `once` on the trigger (see section 30).
   -------------------------------------------------------------------------- */

;(function () {
  var NS = 'http://www.w3.org/2000/svg'
  var SHAPES = 'path, line, polyline, polygon, circle, ellipse, rect'
  var DEFAULTS = { duration: 3, start: 'top 75%', stroke: 1 }
  var uid = 0 // unique ids for the fill kind's generated masks

  // Figma inside-stroke export: build a stroked line out of the mask shape
  function lineFromMask(svg, strokeWidth) {
    var shape = svg.querySelector('mask path')
    if (!shape) return null
    var fills = toArray('path', svg).filter(function (p) {
      return !p.closest('mask')
    })
    // The line takes the fill's rendered look, wherever it comes from —
    // attributes, a class, inline style, currentColor, an opacity on a
    // parent group — so nothing changes colour or transparency
    var look = fills[0] && getComputedStyle(fills[0])
    var color = look && look.fill !== 'none' ? look.fill : 'currentColor'

    // Inline styles, not presentation attributes: a stylesheet rule for
    // the svg's paths (a Webflow class, say) would override attributes and
    // could hand the line a fill or another colour
    var line = document.createElementNS(NS, 'path')
    line.setAttribute('d', shape.getAttribute('d'))
    line.style.fill = 'none'
    line.style.stroke = color
    line.style.strokeOpacity = look ? look.fillOpacity : '1'
    if (look && look.opacity !== '1') line.style.opacity = look.opacity
    line.style.strokeWidth = strokeWidth
    line.style.strokeLinecap = 'round'
    line.style.strokeLinejoin = 'round'
    // Next to the fill, not at the root: an opacity on a parent <g> is not
    // part of computed style — it applies to the group as a whole, so the
    // line has to live inside that same group to get it
    if (fills[0]) fills[0].parentNode.insertBefore(line, fills[0].nextSibling)
    else svg.appendChild(line)

    fills.forEach(function (p) {
      p.style.display = 'none'
    })
    return [line]
  }

  // Plain export: whatever already has a stroke
  function strokedShapes(svg) {
    return toArray(SHAPES, svg).filter(function (el) {
      var stroke = getComputedStyle(el).stroke
      return el.getTotalLength && stroke && stroke !== 'none'
    })
  }

  // Figma "outline stroke" export: no mask, no strokes — the visible shape
  // is a fill tracing a thick line. Drawing its hairline contour reads as
  // nothing, so instead the fill itself is revealed along its own course:
  // each fill path goes behind a <mask> holding the same path stroked in
  // white, wide enough to cover the doodle's line thickness, and it is
  // that mask stroke the timeline dash-animates — the filled shape appears
  // progressively, as if a pen were laying it down
  function linesFromFills(svg) {
    var fills = toArray('path', svg).filter(function (p) {
      return !p.closest('mask') && getComputedStyle(p).fill !== 'none'
    })
    if (!fills.length) return null

    // Cover width: data-draw-svg-stroke if given, else 12% of the viewBox.
    // The outline runs along ONE edge of the doodle's line at a time, so
    // the cover must swallow the full line thickness from that one edge
    // (half the cover reaches across) — then a single pass paints the
    // complete line and the reveal looks right at every moment, not only
    // at the end
    var vb = svg.viewBox && svg.viewBox.baseVal
    var cover = parseFloat(svg.getAttribute('data-draw-svg-stroke'))
    if (!cover) cover = vb && vb.width ? Math.max(vb.width, vb.height) * 0.12 : 120

    var defs = document.createElementNS(NS, 'defs')
    svg.insertBefore(defs, svg.firstChild)

    var lines = fills.map(function (p) {
      var id = 'draw-svg-fill-' + uid++
      var mask = document.createElementNS(NS, 'mask')
      mask.setAttribute('id', id)
      if (vb && vb.width) {
        // The default mask region hugs the shape's bounding box; pad it so
        // the wide stroke's overhang is never clipped
        mask.setAttribute('maskUnits', 'userSpaceOnUse')
        mask.setAttribute('x', vb.x - cover)
        mask.setAttribute('y', vb.y - cover)
        mask.setAttribute('width', vb.width + cover * 2)
        mask.setAttribute('height', vb.height + cover * 2)
      }
      var line = document.createElementNS(NS, 'path')
      line.setAttribute('d', p.getAttribute('d'))
      line.style.fill = 'none'
      line.style.stroke = '#fff'
      line.style.strokeWidth = cover
      line.style.strokeLinecap = 'round'
      line.style.strokeLinejoin = 'round'
      mask.appendChild(line)
      defs.appendChild(mask)
      p.setAttribute('mask', 'url(#' + id + ')')
      // The outline goes down one side of the line and back the other;
      // with the cover painting the full thickness in one pass, the way
      // back adds nothing visible — the pen stops at the far end (half
      // the loop), so animation time maps to what the eye sees
      line._halfDraw = true
      return line
    })

    return {
      lines: lines,
      // The mask exists only for the reveal. Once the pen is done it is
      // torn down completely, so the svg is back to its untouched self —
      // no generated nodes, no risk of the wide stroke under-covering an
      // odd corner of the shape
      finish: function () {
        fills.forEach(function (p) {
          p.removeAttribute('mask')
        })
        if (defs.parentNode) defs.parentNode.removeChild(defs)
      },
    }
  }

  function setup(svg) {
    var duration =
      parseFloat(svg.getAttribute('data-draw-svg-duration')) ||
      DEFAULTS.duration
    var start = svg.getAttribute('data-draw-svg-start') || DEFAULTS.start
    var strokeWidth =
      parseFloat(svg.getAttribute('data-draw-svg-stroke')) || DEFAULTS.stroke
    var triggerSel = svg.getAttribute('data-draw-svg-trigger')
    var trigger = (triggerSel && svg.closest(triggerSel)) || svg

    var finish = null
    var lines = lineFromMask(svg, strokeWidth) || strokedShapes(svg)
    if (!lines.length) {
      var made = linesFromFills(svg)
      if (made) {
        lines = made.lines
        finish = made.finish
      }
    }
    var total = 0
    lines.forEach(function (line) {
      line._length = line.getTotalLength()
      // Where the pen stops: 0 for a real line, halfway for a fill-kind
      // outline (the return leg would repaint what is already visible)
      line._target = line._halfDraw ? line._length / 2 : 0
      total += line._length - line._target
    })
    if (!total) return

    lines.forEach(function (line) {
      gsap.set(line, {
        strokeDasharray: line._length,
        strokeDashoffset: line._length,
      })
    })

    // Linear timeline, one ease over the whole run
    var tl = gsap.timeline({ paused: true })
    lines.forEach(function (line) {
      tl.to(line, {
        strokeDashoffset: line._target,
        duration: (line._length - line._target) / total,
        ease: 'none',
      })
    })

    var drawn = false
    ScrollTrigger.create({
      trigger: trigger,
      start: start,
      onEnter: function () {
        if (drawn) return
        drawn = true
        gsap.to(tl, {
          progress: 1,
          duration: duration,
          ease: 'power1.inOut',
          onComplete: finish,
        })
      },
    })
  }

  onReady(function () {
    if (!window.gsap || !window.ScrollTrigger) return

    toArray('svg.milk-honey-intro__dec').forEach(function (svg) {
      if (!svg.hasAttribute('data-draw-svg'))
        svg.setAttribute('data-draw-svg', '')
    })

    toArray('svg[data-draw-svg]').forEach(setup)
  })
})()

/* --------------------------------------------------------------------------
   33. Team order editor. A team grid marked data-cms-order (its own page,
   not the public one) turns into a sortable list: the client drags members
   into place and saves — no more editing an "order" number per item in
   the CMS.
   - Pointer-driven drag (mouse, pen, touch): the pressed card stays in the
     flow as a dimmed placeholder while a fixed ghost follows the pointer;
     whenever the pointer is over another grid slot the placeholder is moved
     to that slot in the DOM and the grid reflows with a FLIP tween, so the
     new order is always the real DOM order. Slots are the cards' layout
     rects captured when the drag starts (in page coordinates, so wheel
     scrolling mid-drag is fine): hit-testing the live elements would see
     cards still gliding through their FLIP tween and oscillate.
   - CSS counters (app.sass) number the cards in DOM order, so the numbers
     follow the drag for free.
   - A fixed bar shows the state and holds Reset / Save. Save POSTs the
     members in order to the mh-team-order worker (worker-team-order/, its
     own deploy, separate from the contact-form worker), which writes 1..n
     into the collection's order field (staged + live). A member is
     identified by data-cms-slug when the page provides it, else by its
     name (data-modal-contact-name). The shared key comes from the
     data-cms-order attribute's value (the page is password-protected in
     Webflow, so the key travels only to people who may edit); with an
     empty attribute it is asked once and kept in localStorage, and a 401
     clears that and asks again.
   -------------------------------------------------------------------------- */

;(function () {
  var WORKER_URL = 'https://mh-team-order.jaclyn-757.workers.dev/team-order'
  var KEY_STORAGE = 'mh-team-order-key'
  var DRAG_THRESHOLD = 6 // px of movement before a press becomes a drag
  var FLIP_DURATION = 0.25

  onReady(function () {
    var list = document.querySelector('.music-intro-list[data-cms-order]')
    if (!list) return

    var ITEM = '.music-intro-list__item'
    if (!list.querySelector(ITEM)) return

    list.classList.add('is-order-editor')

    // The block's custom cursor ("send message") makes no sense here;
    // section 5 re-reads the attribute on enter, so dropping it is enough
    var cursorHost = list.closest('[data-custom-cursor]')
    if (cursorHost) cursorHost.removeAttribute('data-custom-cursor')

    // Native image dragging would fight the pointer drag
    list.addEventListener('dragstart', function (e) {
      e.preventDefault()
    })

    function items() {
      return toArray(ITEM, list)
    }

    function keyOf(item) {
      return (
        item.getAttribute('data-cms-slug') ||
        item.getAttribute('data-modal-contact-name') ||
        ''
      )
    }

    function currentOrder() {
      return items().map(keyOf)
    }

    var savedOrder = currentOrder()

    function isDirty() {
      var now = currentOrder()
      if (now.length !== savedOrder.length) return true
      for (var i = 0; i < now.length; i++)
        if (now[i] !== savedOrder[i]) return true
      return false
    }

    /* ---- status bar -------------------------------------------------- */

    var bar = document.createElement('div')
    bar.className = 'team-order-bar'
    bar.innerHTML =
      '<div class="team-order-bar__text"></div>' +
      '<button type="button" class="team-order-bar__btn is-reset">Reset</button>' +
      '<button type="button" class="team-order-bar__btn is-save">Save order</button>'
    document.body.appendChild(bar)

    var barText = bar.querySelector('.team-order-bar__text')
    var btnReset = bar.querySelector('.is-reset')
    var btnSave = bar.querySelector('.is-save')

    function setStatus(text, state) {
      barText.textContent = text
      bar.classList.remove('is-error', 'is-success', 'is-busy')
      if (state) bar.classList.add('is-' + state)
    }

    function updateButtons() {
      var dirty = isDirty()
      btnSave.disabled = !dirty
      btnReset.disabled = !dirty
      return dirty
    }

    // Neutral state text + buttons (error/success texts are set by save())
    function refreshBar() {
      var dirty = updateButtons()
      if (dirty) setStatus('Order changed — not saved yet')
      else if (!bar.classList.contains('is-success'))
        setStatus('Drag a member to change the order')
    }

    refreshBar()

    // Leaving with unsaved changes gets the browser's confirm dialog
    window.addEventListener('beforeunload', function (e) {
      if (!isDirty()) return
      e.preventDefault()
      e.returnValue = ''
    })

    /* ---- FLIP reflow ------------------------------------------------- */

    function snapshot() {
      var map = new Map()
      items().forEach(function (el) {
        map.set(el, el.getBoundingClientRect())
      })
      return map
    }

    function flip(before) {
      if (!window.gsap) return
      items().forEach(function (el) {
        var was = before.get(el)
        if (!was) return
        var now = el.getBoundingClientRect()
        var dx = was.left - now.left
        var dy = was.top - now.top
        if (!dx && !dy) return
        gsap.fromTo(
          el,
          { x: dx, y: dy },
          { x: 0, y: 0, duration: FLIP_DURATION, ease: 'power2.out', overwrite: true }
        )
      })
    }

    // Re-arrange the DOM to a given key order (used by Reset)
    function applyOrder(keys) {
      var before = snapshot()
      var byKey = new Map()
      items().forEach(function (el) {
        byKey.set(keyOf(el), el)
      })
      keys.forEach(function (key) {
        var el = byKey.get(key)
        if (el) list.appendChild(el)
      })
      flip(before)
    }

    /* ---- drag -------------------------------------------------------- */

    var drag = null // { item, ghost, startX, startY, offsetX, offsetY, slots, active }

    // Layout rects of every grid slot, in page coordinates
    function slotRects() {
      var sx = window.pageXOffset
      var sy = window.pageYOffset
      return items().map(function (el) {
        var r = el.getBoundingClientRect()
        return { left: r.left + sx, top: r.top + sy, right: r.right + sx, bottom: r.bottom + sy }
      })
    }

    function slotAt(e) {
      var x = e.pageX
      var y = e.pageY
      var slots = drag.slots
      for (var i = 0; i < slots.length; i++) {
        var r = slots[i]
        if (x >= r.left && x <= r.right && y >= r.top && y <= r.bottom) return i
      }
      return -1
    }

    function ghostFor(item, rect) {
      var ghost = item.cloneNode(true)
      ghost.className = 'team-order-ghost'
      ghost.removeAttribute('style')
      ghost.style.width = rect.width + 'px'
      ghost.style.height = rect.height + 'px'
      document.body.appendChild(ghost)
      return ghost
    }

    function moveGhost(e) {
      var x = e.clientX - drag.offsetX
      var y = e.clientY - drag.offsetY
      if (window.gsap) gsap.set(drag.ghost, { x: x, y: y })
      else drag.ghost.style.transform = 'translate(' + x + 'px,' + y + 'px)'
    }

    function startDrag(e) {
      drag.active = true
      drag.slots = slotRects()
      var rect = drag.item.getBoundingClientRect()
      drag.offsetX = drag.startX - rect.left
      drag.offsetY = drag.startY - rect.top
      drag.ghost = ghostFor(drag.item, rect)
      drag.item.classList.add('is-dragging')
      document.body.classList.add('is-team-dragging')
      moveGhost(e)
    }

    function onMove(e) {
      if (!drag) return
      if (!drag.active) {
        var dx = e.clientX - drag.startX
        var dy = e.clientY - drag.startY
        if (dx * dx + dy * dy < DRAG_THRESHOLD * DRAG_THRESHOLD) return
        startDrag(e)
      }
      e.preventDefault()
      moveGhost(e)

      var to = slotAt(e)
      if (to === -1) return
      var all = items()
      var from = all.indexOf(drag.item)
      if (from === -1 || from === to) return

      // Put the placeholder into the slot under the pointer: forward means
      // after the card currently holding that slot, backward means before
      var target = all[to]
      var before = snapshot()
      if (to > from) target.after(drag.item)
      else target.before(drag.item)
      flip(before)
    }

    function endDrag(e) {
      if (!drag) return
      var d = drag
      drag = null
      if (d.item.hasPointerCapture && d.item.hasPointerCapture(e.pointerId))
        d.item.releasePointerCapture(e.pointerId)
      if (!d.active) return

      d.item.classList.remove('is-dragging')
      document.body.classList.remove('is-team-dragging')

      // The ghost glides into the placeholder's final spot, then goes
      var rect = d.item.getBoundingClientRect()
      if (window.gsap) {
        gsap.to(d.ghost, {
          x: rect.left,
          y: rect.top,
          duration: 0.2,
          ease: 'power2.out',
          onComplete: function () {
            d.ghost.remove()
          },
        })
      } else d.ghost.remove()

      refreshBar()
    }

    list.addEventListener('pointerdown', function (e) {
      if (e.button !== 0 || drag) return
      var item = e.target.closest(ITEM)
      if (!item || !list.contains(item)) return
      drag = {
        item: item,
        startX: e.clientX,
        startY: e.clientY,
        active: false,
      }
      if (item.setPointerCapture) item.setPointerCapture(e.pointerId)
    })
    list.addEventListener('pointermove', onMove)
    list.addEventListener('pointerup', endDrag)
    list.addEventListener('pointercancel', endDrag)

    // A drag must not end as a click on the card (section 4 would open a
    // modal if the item carried data-modal-show — it does not here, but
    // the Webflow item may be a link in the Designer)
    list.addEventListener(
      'click',
      function (e) {
        if (e.target.closest(ITEM)) e.preventDefault()
      },
      true
    )

    /* ---- save -------------------------------------------------------- */

    // Key baked into the page wins; the prompt is the fallback
    var pageKey = (list.getAttribute('data-cms-order') || '').trim()

    function getKey(force) {
      if (pageKey) return pageKey
      var key = null
      try {
        key = force ? null : window.localStorage.getItem(KEY_STORAGE)
      } catch (err) {}
      if (!key) {
        key = window.prompt('Access key for saving the team order:')
        if (!key) return null
        try {
          window.localStorage.setItem(KEY_STORAGE, key)
        } catch (err) {}
      }
      return key
    }

    function forgetKey() {
      try {
        window.localStorage.removeItem(KEY_STORAGE)
      } catch (err) {}
    }

    function save(retry) {
      if (!isDirty() || bar.classList.contains('is-busy')) return
      var key = getKey(retry)
      if (!key) return

      var order = items().map(function (el) {
        return {
          slug: el.getAttribute('data-cms-slug') || '',
          name: el.getAttribute('data-modal-contact-name') || '',
        }
      })

      setStatus('Saving…', 'busy')
      btnSave.disabled = true
      btnReset.disabled = true

      fetch(WORKER_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Order-Key': key },
        body: JSON.stringify({ order: order }),
      })
        .then(function (res) {
          return res.json().then(function (data) {
            return { status: res.status, data: data || {} }
          })
        })
        .then(function (res) {
          if (res.status === 401) {
            forgetKey()
            setStatus(
              pageKey
                ? 'Access key on this page is wrong — update the data-cms-order attribute'
                : 'Wrong access key — try again',
              'error'
            )
            updateButtons()
            return
          }
          if (res.status !== 200 || !res.data.ok) {
            var why = res.data.error || 'error ' + res.status
            if (res.data.message) why += ': ' + res.data.message
            if (res.data.items) why += ': ' + res.data.items.join(', ')
            setStatus('Could not save (' + why + ')', 'error')
            updateButtons()
            return
          }
          savedOrder = currentOrder()
          setStatus(
            'Saved — ' + res.data.updated + ' member' +
              (res.data.updated === 1 ? '' : 's') + ' updated and published',
            'success'
          )
          btnSave.disabled = true
          btnReset.disabled = true
        })
        .catch(function () {
          setStatus('Could not reach the server', 'error')
          updateButtons()
        })
    }

    btnSave.addEventListener('click', function () {
      save(false)
    })
    btnReset.addEventListener('click', function () {
      if (bar.classList.contains('is-busy')) return
      applyOrder(savedOrder)
      refreshBar()
    })
  })
})()
